import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthRequiredModal from "./AuthRequiredModal";
import Modal from "./Modal";
import BookingCalendar from "../calendar/BookingCalendar";
import { useAuth } from "../../auth/authStore";
import { useToast } from "../../ui/toast/ToastProvider";
import { psychologistsApi } from "../../api/psychologistsApi";
import { sessionsApi } from "../../api/sessionsApi";
import { blogApi } from "../../api/blogApi";
import { chatApi } from "../../api/chatApi";
import { filesApi } from "../../api/filesApi";
import { buildSlotEvents } from "../calendar/calendarUtils";
import { resolveUrl } from "../../utils/urls";
import { openExternalUrl, sanitizeLinkHref } from "../../utils/safeUrl";
import {
  isBookingTemporarilyDisabled,
  triggerMaintenanceBanner,
} from "../../utils/maintenanceBanner";
import { REACTION_OPTIONS } from "../../ui/reactions/reactionKinds";
import { normalizeReactionValue } from "../../ui/reactions/reactionModel";

const CHAT_POLICY_ANY_AUTHORIZED = "ANY_AUTHORIZED";
const CHAT_POLICY_ONLY_CLIENTS_WITH_PAID_HISTORY = "ONLY_CLIENTS_WITH_PAID_HISTORY";
const CHAT_POLICY_NOBODY = "NOBODY";
const CHAT_POLICY_VALUES = new Set([
  CHAT_POLICY_ANY_AUTHORIZED,
  CHAT_POLICY_ONLY_CLIENTS_WITH_PAID_HISTORY,
  CHAT_POLICY_NOBODY,
]);

const STORY_REACTION_EMOJIS = REACTION_OPTIONS.slice(0, 5).map((item) => item.emoji);

function safeText(v) {
  return String(v || "").replace(/\s+/g, " ").trim();
}

function normalizeChatPolicy(value) {
  const policy = String(value || "").trim().toUpperCase();
  return CHAT_POLICY_VALUES.has(policy) ? policy : "";
}

function resolveChatPolicy(psychologist) {
  return (
    normalizeChatPolicy(psychologist?.inboundPolicy) ||
    normalizeChatPolicy(psychologist?.directMessagesPolicy) ||
    normalizeChatPolicy(psychologist?.chatPolicy) ||
    normalizeChatPolicy(psychologist?.chatPermission?.inboundPolicy) ||
    normalizeChatPolicy(psychologist?.chatPermission?.policy) ||
    ""
  );
}

function resolveExplicitChatAccess(psychologist) {
  const candidates = [
    psychologist?.canWriteToChat,
    psychologist?.canWrite,
    psychologist?.canOpenChat,
    psychologist?.chatPermission?.canWrite,
    psychologist?.chatPermission?.allowed,
  ];
  const explicit = candidates.find((value) => typeof value === "boolean");
  return typeof explicit === "boolean" ? explicit : null;
}

function normalizeRole(rawRole) {
  return String(rawRole || "")
    .replace(/^ROLE_/, "")
    .toUpperCase();
}

function isStaffRole(rawRole) {
  const role = normalizeRole(rawRole);
  return role === "ADMIN" || role === "MODERATOR";
}

function extractErrorCode(err) {
  const code = err?.code ?? err?.errorCode ?? err?.error?.code ?? "";
  return String(code || "").trim().toUpperCase();
}

function policyRestrictionText(policy, fallback = "") {
  if (policy === CHAT_POLICY_ONLY_CLIENTS_WITH_PAID_HISTORY) {
    return "Психолог принимает сообщения только от клиентов с оплаченной или завершенной историей.";
  }
  if (policy === CHAT_POLICY_NOBODY) {
    return "Психолог временно отключил входящие сообщения.";
  }
  return fallback || "Написать этому психологу сейчас нельзя.";
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  if (typeof value === "string") {
    const s = safeText(value);
    if (!s) return [];
    return s.split(",").map((x) => safeText(x)).filter(Boolean);
  }
  return [];
}

function extractPageContent(raw) {
  if (Array.isArray(raw?.items?.content)) return raw.items.content;
  if (Array.isArray(raw?.item?.content)) return raw.item.content;
  if (Array.isArray(raw?.item?.items)) return raw.item.items;
  if (Array.isArray(raw?.items)) return raw.items;
  if (Array.isArray(raw?.item)) return raw.item;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.content)) return raw.content;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
}

function unwrapOkPayload(raw) {
  if (raw && typeof raw === "object" && raw.ok === true) {
    if (raw.item != null) return raw.item;
    if (raw.items != null) return raw.items;
  }
  return raw;
}

function readBooleanField(raw, key, fallback = false) {
  const source = unwrapOkPayload(raw);
  if (source && typeof source === "object" && typeof source[key] === "boolean") {
    return source[key];
  }
  return fallback;
}

function readCountField(raw, key, fallback = 0) {
  const source = unwrapOkPayload(raw);
  const n = Number(source?.[key]);
  return Number.isFinite(n) ? n : fallback;
}

function initialsFromName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "ПС";
  const first = parts[0]?.[0] || "";
  const second = parts[1]?.[0] || parts[0]?.[1] || "";
  return `${first}${second}`.toUpperCase();
}

function formatDateRu(v, { withYear = true } = {}) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return safeText(v);
  const opts = withYear
    ? { day: "2-digit", month: "long", year: "numeric" }
    : { day: "2-digit", month: "short" };
  return d.toLocaleDateString("ru-RU", opts).replace(" г.", "");
}

function formatDaySlot(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d
    .toLocaleDateString("ru-RU", { weekday: "short", day: "2-digit", month: "short" })
    .replace(".", "")
    .replace(/\s+/g, " ")
    .trim();
}

function formatTimeSlot(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function formatCount(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return "0";
  return n.toLocaleString("ru-RU");
}

function yearsWordRu(value) {
  const n = Math.abs(Number(value));
  if (!Number.isFinite(n) || n <= 0) return "лет";
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return "лет";
  if (mod10 === 1) return "год";
  if (mod10 >= 2 && mod10 <= 4) return "года";
  return "лет";
}

function formatYearsPlusRu(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `${n}+ ${yearsWordRu(n)}`;
}

function moneyParts(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return { num: "—", cur: "₽" };
  return { num: n.toLocaleString("ru-RU"), cur: "₽" };
}

function starsText(count) {
  const n = Math.max(0, Math.min(5, Number(count) || 0));
  return "★".repeat(n) + "☆".repeat(5 - n);
}

function postTopicClass(variant) {
  if (variant === "teal") return "tp-teal";
  if (variant === "amber") return "tp-amber";
  if (variant === "purple") return "tp-purple";
  if (variant === "coral") return "tp-purple";
  return "tp-teal";
}

function variantByIndex(idx) {
  const seq = ["teal", "amber", "purple", "coral"];
  return seq[idx % seq.length];
}

function blogPostPath(post) {
  if (!post) return "";
  const id = post?.id ?? post?.postId ?? null;
  const slug = safeText(post?.slug);
  if (id && slug) return `/blog/${id}-${slug}`;
  if (slug) return `/blog/${slug}`;
  return "";
}

function parsePositiveId(value) {
  if (value === null || value === undefined) return null;
  const raw = safeText(value);
  if (!raw) return null;
  const match = raw.match(/^(\d+)/);
  const n = Number(match ? match[1] : raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function parseForumTopicIdFromUrl(rawUrl) {
  const value = safeText(rawUrl);
  if (!value) return null;
  const match = value.match(/\/forum\/topics\/(\d+)/i);
  return parsePositiveId(match ? match[1] : null);
}

function forumTopicPath(topicId) {
  const id = parsePositiveId(topicId);
  if (!id) return "/forum";
  return `/forum/topics/${id}`;
}

function mapBlogPost(raw, author, idx) {
  const category = safeText(raw?.category || raw?.categoryTitle || raw?.topic || raw?.theme || "");
  const topic = category ? `#${category.toLowerCase()}` : "#психология";
  return {
    id: raw?.id ?? raw?.postId ?? `post-${idx}`,
    title: safeText(raw?.title || ""),
    author: safeText(raw?.author?.name || raw?.authorName || author || "Психолог"),
    date: formatDateRu(raw?.publishedAt || raw?.createdWhen || raw?.createdAt || raw?.date),
    topic,
    topicVariant: variantByIndex(idx),
    text: safeText(raw?.description || raw?.excerpt || raw?.text || raw?.previewText || ""),
    likes: Number(raw?.likesCount ?? raw?.reactionsCount ?? raw?.likes ?? 0) || 0,
    likedByMe: Boolean(raw?.likedByMe ?? raw?.isLiked ?? raw?.liked ?? raw?.myReaction),
    comments: Number(raw?.commentsCount ?? raw?.comments ?? 0) || 0,
    previewImageUrl: resolveUrl(raw?.previewImageUrl || raw?.image || raw?.coverImageUrl || ""),
    url: safeText(raw?.url || blogPostPath(raw)),
  };
}

function mapForumAnswer(raw, idx) {
  const answerText = safeText(raw?.answerText || raw?.answer || raw?.content || "");
  const questionText = safeText(raw?.questionText || raw?.description || raw?.text || raw?.questionContent || "");
  const answered = Boolean(raw?.answered || answerText);
  const date = formatDateRu(raw?.createdAt || raw?.publishedAt || raw?.date || raw?.createdWhen);
  const views = Number(raw?.viewsCount ?? raw?.views ?? 0) || 0;
  const likes = Number(raw?.reactionsCount ?? raw?.likesCount ?? raw?.likes ?? 0) || 0;
  const section = safeText(raw?.sectionTitle || raw?.section || "");
  const author = safeText(raw?.authorName || raw?.userName || raw?.author || "");
  const metaParts = [section, author, date, views > 0 ? `${views} просмотров` : "", likes > 0 ? `${likes} реакций` : ""]
    .filter(Boolean)
    .join(" · ");
  const rawUrl = safeText(raw?.url || raw?.topicUrl || raw?.linkUrl || raw?.link || raw?.topicLink || "");
  const commentId = parsePositiveId(raw?.commentId ?? raw?.answerId ?? raw?.comment?.id);
  const explicitTopicId = parsePositiveId(
    raw?.topicId ?? raw?.topic?.id ?? raw?.questionId ?? raw?.forumTopicId
  );
  const fallbackTopicId = commentId ? null : parsePositiveId(raw?.id);
  const topicId =
    explicitTopicId ?? parseForumTopicIdFromUrl(rawUrl) ?? fallbackTopicId;
  const routeUrl = rawUrl || forumTopicPath(topicId);

  return {
    id: raw?.commentId ?? raw?.id ?? raw?.answerId ?? `forum-${idx}`,
    title: safeText(raw?.topicTitle || raw?.questionTitle || raw?.title || raw?.question || "Вопрос"),
    text: questionText,
    answer: answerText,
    badge: answered ? "Отвечено" : "Новый",
    badgeVariant: answered ? "answered" : "new",
    meta: metaParts,
    topicId,
    commentId,
    url: routeUrl,
  };
}

function mapReview(raw, idx) {
  const sessionsCount = Number(raw?.sessionsCount ?? raw?.completedSessions ?? raw?.sessions ?? 0) || 0;
  return {
    id: raw?.id ?? raw?.reviewId ?? `review-${idx}`,
    name: safeText(raw?.clientName || raw?.authorName || raw?.name || "Клиент"),
    verified: Boolean(raw?.verified || raw?.isVerified || raw?.verifiedClient),
    stars: Number(raw?.rating ?? raw?.stars ?? raw?.score ?? 0) || 0,
    text: safeText(raw?.text || raw?.comment || raw?.review || ""),
    date: formatDateRu(raw?.createdAt || raw?.date || raw?.publishedAt) || "",
    sessions: sessionsCount > 0 ? `${sessionsCount} сессий` : "",
  };
}

function mapEducation(raw, idx) {
  const title = safeText(raw?.title || raw?.name || raw?.institution || raw?.program || "");
  const subtitle = [safeText(raw?.specialization || raw?.degree || raw?.issuer), safeText(raw?.year || raw?.graduationYear)]
    .filter(Boolean)
    .join(" · ");
  return {
    id: raw?.id ?? `edu-${idx}`,
    icon: "🎓",
    name: title || "Образование",
    sub: subtitle || "Подтверждено психологом",
  };
}

function mapCertificate(raw, idx) {
  const verificationStatus = safeText(raw?.verificationStatus || raw?.status).toUpperCase();
  const year = safeText(
    raw?.year ||
      raw?.issuedYear ||
      (raw?.issuedAt ? formatDateRu(raw?.issuedAt, { withYear: true }) : "")
  );
  return {
    id: raw?.id ?? `cert-${idx}`,
    icon: "📜",
    name: safeText(raw?.title || raw?.name || raw?.certificateName || "Сертификат"),
    org: safeText(raw?.issuer || raw?.organization || raw?.issuedBy || ""),
    year,
    verified: Boolean(raw?.verified || raw?.isVerified),
    verificationStatus,
    fileName: safeText(
      raw?.fileName ||
      raw?.filename ||
      raw?.documentName ||
      raw?.attachmentName ||
      raw?.originalFileName ||
      raw?.original_filename
    ),
    mimeType: safeText(
      raw?.mimeType ||
      raw?.mime_type ||
      raw?.contentType ||
      raw?.content_type ||
      raw?.fileMimeType ||
      raw?.file_mime_type ||
      raw?.documentMimeType
    ),
    documentUrl: resolveUrl(safeText(raw?.documentUrl || raw?.fileUrl || raw?.url || "")),
  };
}

const APPROVED_CERTIFICATE_STATUS_SET = new Set([
  "VERIFIED",
  "APPROVED",
  "APPROVE",
  "PUBLISHED",
  "PUBLIC",
  "ACTIVE",
  "ACCEPTED",
]);

function isCertificateModerated(cert) {
  if (!cert || typeof cert !== "object") return false;
  if (Boolean(cert.verified)) return true;
  const status = safeText(cert.verificationStatus || cert.status).toUpperCase();
  return APPROVED_CERTIFICATE_STATUS_SET.has(status);
}

function detectCertificateMediaType(urlValue, cert) {
  const url = safeText(urlValue);
  const lowerUrl = url.toLowerCase();
  let decodedUrl = lowerUrl;
  try {
    decodedUrl = decodeURIComponent(lowerUrl);
  } catch {
    decodedUrl = lowerUrl;
  }

  const fileName = safeText(cert?.fileName || cert?.name).toLowerCase();
  const mime = safeText(cert?.mimeType).toLowerCase();

  const hasPdfExt = /\.pdf(?:[?#].*)?$/i.test(url) || /\.pdf$/i.test(fileName);
  const hasImageExt =
    /\.(png|jpe?g|gif|webp|svg)(?:[?#].*)?$/i.test(url) ||
    /\.(png|jpe?g|gif|webp|svg)$/i.test(fileName);
  const urlHintsPdf =
    /application(?:%2f|\/)pdf/i.test(lowerUrl) ||
    /[?&](?:response-content-type|content-type|mime(?:type)?|type)=application(?:%2f|\/)pdf/i.test(decodedUrl);
  const urlHintsImage =
    /image(?:%2f|\/)(?:png|jpe?g|gif|webp|svg\+xml)/i.test(lowerUrl) ||
    /[?&](?:response-content-type|content-type|mime(?:type)?|type)=image(?:%2f|\/)(?:png|jpe?g|gif|webp|svg\+xml)/i.test(decodedUrl);

  if (mime.includes("pdf") || hasPdfExt || urlHintsPdf) return "pdf";
  if (mime.startsWith("image/") || hasImageExt || urlHintsImage) return "image";
  return "other";
}

function canEmbedDocumentFrame(urlValue) {
  const raw = safeText(urlValue);
  if (!raw) return false;
  if (/^(data:|blob:)/i.test(raw)) return true;
  if (typeof window === "undefined") return false;
  try {
    const parsed = new URL(raw, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
}

function mapTest(raw, idx) {
  return {
    id: raw?.id ?? raw?.testId ?? `test-${idx}`,
    title: safeText(raw?.title || raw?.name || "Психологический тест"),
    description: safeText(raw?.description || raw?.shortDescription || ""),
    questionsCount: Number(raw?.questionsCount ?? raw?.itemsCount ?? 0) || 0,
    durationMinutes: Number(raw?.durationMinutes ?? raw?.estimatedMinutes ?? 0) || 0,
    url: safeText(raw?.url || raw?.externalUrl || ""),
  };
}

function normalizeContactPoints(psychologist) {
  const normalizeType = (rawType, value, url) => {
    const type = safeText(rawType).toLowerCase();
    if (type.includes("mail")) return "email";
    if (type.includes("phone") || type.includes("tel")) return "phone";
    if (type.includes("telegram") || type === "tg") return "telegram";
    if (type.includes("site") || type.includes("web") || type.includes("url")) return "website";
    if (type.includes("region") || type.includes("location") || type.includes("city") || type.includes("address")) return "location";
    const sample = safeText(url || value);
    if (/^mailto:/i.test(sample) || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(sample)) return "email";
    if (/^tel:/i.test(sample) || /^\+?\d[\d\s\-()]{5,}$/.test(sample)) return "phone";
    if (/(?:^|\/\/)(?:www\.)?(?:t\.me|telegram\.me)\//i.test(sample) || /^@[A-Z0-9_]{3,}$/i.test(sample)) return "telegram";
    if (/^(?:https?:\/\/|www\.)/i.test(sample)) return "website";
    return type || "text";
  };

  const defaultLabelByType = (type) => {
    if (type === "email") return "Email";
    if (type === "phone") return "Телефон";
    if (type === "telegram") return "Telegram";
    if (type === "website") return "Сайт";
    if (type === "location") return "Регион";
    return "Контакт";
  };

  const normalizeHref = (type, rawUrl, rawValue) => {
    const source = safeText(rawUrl || rawValue);
    if (!source || type === "location" || type === "text") return "";

    if (type === "email") {
      const email = source.replace(/^mailto:/i, "");
      return email ? `mailto:${email}` : "";
    }

    if (type === "phone") {
      const phone = source.replace(/^tel:/i, "");
      return phone ? `tel:${phone}` : "";
    }

    if (type === "telegram") {
      if (/^https?:\/\//i.test(source)) return source;
      if (/^(?:www\.)?(?:t\.me|telegram\.me)\//i.test(source)) return `https://${source.replace(/^www\./i, "")}`;
      if (/^@[A-Z0-9_]{3,}$/i.test(source)) return `https://t.me/${source.slice(1)}`;
      if (/^[A-Z0-9_]{3,}$/i.test(source)) return `https://t.me/${source}`;
      return source;
    }

    if (/^https?:\/\//i.test(source)) return source;
    if (/^[A-Z0-9.-]+\.[A-Z]{2,}(?:\/.*)?$/i.test(source)) return `https://${source}`;
    return source;
  };

  const points = toArray(psychologist?.contactPoints).map((item) => {
    const type = normalizeType(item?.type, item?.value, item?.url);
    return {
      type,
      label: safeText(item?.label || defaultLabelByType(type)),
      value: safeText(item?.value || item?.url || ""),
      url: normalizeHref(type, item?.url, item?.value),
    };
  });

  const fromFlat = [
    psychologist?.publicEmail
      ? { type: "email", label: "Email", value: safeText(psychologist.publicEmail), url: `mailto:${safeText(psychologist.publicEmail)}` }
      : null,
    psychologist?.telegramUrl
      ? { type: "telegram", label: "Telegram", value: safeText(psychologist.telegramUrl), url: safeText(psychologist.telegramUrl) }
      : null,
    psychologist?.contactPhone
      ? { type: "phone", label: "Телефон", value: safeText(psychologist.contactPhone), url: `tel:${safeText(psychologist.contactPhone)}` }
      : null,
    psychologist?.regionLabel
      ? { type: "location", label: "Регион", value: safeText(psychologist.regionLabel), url: "" }
      : null,
  ].filter(Boolean);

  const merged = [...points, ...fromFlat];
  const uniq = new Map();
  merged.forEach((item) => {
    const key = `${item.label}|${item.value}`;
    if (!uniq.has(key) && item.value) uniq.set(key, item);
  });
  return Array.from(uniq.values()).slice(0, 6);
}

function contactDisplayValue(contact) {
  const type = safeText(contact?.type).toLowerCase();
  const rawValue = safeText(contact?.value);
  const rawUrl = safeText(contact?.url);
  const source = rawValue || rawUrl;
  if (!source) return "";

  const parseUrl = (input) => {
    if (!input) return null;
    try {
      return new URL(input);
    } catch {
      try {
        return new URL(`https://${input}`);
      } catch {
        return null;
      }
    }
  };

  const compactUrl = (input) => {
    const parsed = parseUrl(input);
    if (parsed) {
      const host = parsed.hostname.replace(/^www\./i, "");
      const path = parsed.pathname && parsed.pathname !== "/" ? parsed.pathname.replace(/\/+$/g, "") : "";
      return `${host}${path}`;
    }
    return safeText(input).replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/+$/g, "");
  };

  if (type === "email") {
    return source.replace(/^mailto:/i, "");
  }

  if (type === "phone") {
    return source.replace(/^tel:/i, "");
  }

  if (type === "telegram") {
    const tgHandle = source.match(/^@([A-Z0-9_]{3,})$/i)?.[1]
      || source.match(/(?:^|\/\/)(?:www\.)?(?:t\.me|telegram\.me)\/([A-Z0-9_]{3,})/i)?.[1];
    if (tgHandle) return `@${tgHandle}`;
    return compactUrl(source);
  }

  if (type === "website") {
    return compactUrl(source);
  }

  return rawValue;
}

function flattenCalendarSlots(raw) {
  const out = [];

  const pushIso = (iso) => {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return;
    out.push(dt.toISOString());
  };

  const pushDateTime = (date, time) => {
    if (!date || !time) return;
    const iso = `${String(date)}T${String(time).length === 5 ? `${time}:00` : time}`;
    pushIso(iso);
  };

  const rows = extractPageContent(raw);
  rows.forEach((row) => {
    const iso = row?.startAt || row?.startDateTime || row?.dateTime || row?.datetime;
    if (iso) {
      pushIso(iso);
      return;
    }
    pushDateTime(row?.date, row?.time);
  });

  if (Array.isArray(raw?.days)) {
    raw.days.forEach((dayRow) => {
      const date = dayRow?.date;
      toArray(dayRow?.slots || dayRow?.times).forEach((slot) => {
        if (typeof slot === "string") pushDateTime(date, slot);
        else {
          const iso = slot?.startAt || slot?.startDateTime || slot?.dateTime;
          if (iso) pushIso(iso);
          else pushDateTime(slot?.date || date, slot?.time);
        }
      });
    });
  }

  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    Object.keys(raw).forEach((k) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(k)) return;
      const times = raw[k];
      if (Array.isArray(times)) {
        times.forEach((t) => {
          if (typeof t === "string") pushDateTime(k, t);
        });
      }
    });
  }

  const uniqSorted = Array.from(new Set(out)).sort((a, b) => new Date(a) - new Date(b));
  return uniqSorted.slice(0, 3).map((iso) => ({
    iso,
    day: formatDaySlot(iso),
    time: formatTimeSlot(iso),
  }));
}

function buildHighlights(psychologist) {
  const existing = toArray(psychologist?.highlights)
    .map((item, idx) => {
      if (typeof item === "string") {
        return { id: `h-${idx}`, emoji: "✨", label: safeText(item), isNew: idx === 0 };
      }
      return {
        id: item?.id ?? `h-${idx}`,
        emoji: safeText(item?.emoji || "✨"),
        label: safeText(item?.label || item?.title || "Раздел"),
        isNew: Boolean(item?.isNew || item?.is_new),
      };
    })
    .filter((item) => item.label);

  if (existing.length) return existing;

  const sections = psychologist?.sections || {};
  const derived = [];
  if (sections?.hasFaq) derived.push({ id: "faq", emoji: "❓", label: "FAQ" });
  if (sections?.hasPodcast) derived.push({ id: "podcast", emoji: "🎙", label: "Подкаст" });
  if (sections?.hasTests) derived.push({ id: "tests", emoji: "📝", label: "Тесты" });
  if (sections?.hasCourses) derived.push({ id: "courses", emoji: "🏅", label: "Курсы" });
  if (sections?.hasLiterature) derived.push({ id: "literature", emoji: "📚", label: "Литература" });
  if (sections?.hasStories) derived.push({ id: "stories", emoji: "🔴", label: "Сторис", isNew: true });

  return derived.slice(0, 7);
}

function storyBackgroundByIndex(idx) {
  const backgrounds = [
    "linear-gradient(160deg,#3c3489 0%,#7f77dd 55%,#5dcaa5 100%)",
    "linear-gradient(160deg,#7f77dd 0%,#a78bfa 45%,#f0997b 100%)",
    "linear-gradient(160deg,#0f6e56 0%,#5dcaa5 55%,#eeedfe 100%)",
    "linear-gradient(160deg,#993c1d 0%,#f0997b 45%,#faeeda 100%)",
    "linear-gradient(160deg,#2a2d36 0%,#534ab7 60%,#7f77dd 100%)",
  ];
  return backgrounds[idx % backgrounds.length];
}

const editorBackgroundPalette = [
  "linear-gradient(160deg,#6d28d9,#a855f7,#ec4899)",
  "linear-gradient(160deg,#1e3a5f,#2563eb,#7c3aed)",
  "linear-gradient(160deg,#065f46,#059669,#34d399)",
  "linear-gradient(160deg,#7c2d12,#ea580c,#fbbf24)",
  "linear-gradient(160deg,#0c4a6e,#0284c7,#38bdf8)",
  "linear-gradient(160deg,#4a1d96,#7c3aed,#c084fc)",
  "linear-gradient(160deg,#881337,#e11d48,#fb7185)",
  "linear-gradient(160deg,#1f2937,#374151,#6b7280)",
  "linear-gradient(160deg,#134e4a,#0f766e,#2dd4bf)",
  "linear-gradient(160deg,#713f12,#a16207,#eab308)",
];

const editorStickerPalette = [
  "✨",
  "🔥",
  "💫",
  "🌟",
  "💖",
  "🦋",
  "🌊",
  "🌿",
  "🧠",
  "💡",
  "🎯",
  "🎙",
  "📝",
  "🏅",
  "🌱",
  "🕊",
  "💬",
  "❤️",
  "🙌",
  "👀",
  "💪",
  "🫶",
  "🎵",
  "📚",
  "🌙",
  "☀️",
  "🌈",
  "🍃",
  "🪷",
  "🦚",
  "🐚",
  "🫧",
  "✌️",
  "🤍",
  "💜",
  "🖤",
];

const editorFxCards = [
  { key: "heartbeat", ico: "💓", name: "Сердцебиение", desc: "Двойной пульс ритмично", badge: "Loop", params: [{ key: "spd", label: "Скорость", min: 4, max: 30, step: 1 }, { key: "sc", label: "Размах", min: 105, max: 150, step: 1 }] },
  { key: "spin", ico: "🌀", name: "Кручение ×1", desc: "Один оборот и стоп", badge: "×1", params: [{ key: "spd", label: "Скорость", min: 2, max: 20, step: 1 }] },
  { key: "spinloop", ico: "🔄", name: "Кручение Loop", desc: "Непрерывное вращение", badge: "Loop", params: [{ key: "spd", label: "Скорость", min: 3, max: 30, step: 1 }] },
  { key: "float", ico: "🕊", name: "Парение", desc: "Плавно вверх-вниз", badge: "Loop", params: [{ key: "spd", label: "Скорость", min: 5, max: 40, step: 1 }, { key: "d", label: "Высота", min: 2, max: 30, step: 1 }] },
  { key: "bounce", ico: "⚽", name: "Отскок", desc: "Как мяч — вверх и отскок", badge: "Loop", params: [{ key: "spd", label: "Скорость", min: 5, max: 30, step: 1 }, { key: "d", label: "Высота", min: 4, max: 40, step: 1 }] },
  { key: "shake", ico: "⚡", name: "Встряска", desc: "Резкое дрожание раз", badge: "×1", params: [{ key: "spd", label: "Скорость", min: 2, max: 15, step: 1 }, { key: "d", label: "Сила", min: 2, max: 20, step: 1 }] },
  { key: "swing", ico: "🎸", name: "Маятник", desc: "Качается влево-вправо", badge: "Loop", params: [{ key: "spd", label: "Скорость", min: 4, max: 25, step: 1 }, { key: "ang", label: "Угол", min: 3, max: 40, step: 1 }] },
  { key: "pulse", ico: "✨", name: "Мерцание", desc: "Гаснет и светится", badge: "Loop", params: [{ key: "spd", label: "Скорость", min: 5, max: 30, step: 1 }, { key: "sc", label: "Мин. яркость", min: 0, max: 70, step: 1 }] },
  { key: "zoom", ico: "🔍", name: "Пульс-зум", desc: "Ритмично растёт", badge: "Loop", params: [{ key: "spd", label: "Скорость", min: 5, max: 30, step: 1 }, { key: "sc", label: "Размер", min: 105, max: 175, step: 1 }] },
  { key: "jelly", ico: "🍮", name: "Желе", desc: "Деформируется упруго", badge: "Loop", params: [{ key: "spd", label: "Скорость", min: 3, max: 20, step: 1 }] },
  { key: "glitch", ico: "📺", name: "Глитч", desc: "Цифровые рывки", badge: "Loop", params: [{ key: "spd", label: "Скорость", min: 5, max: 30, step: 1 }] },
  { key: "neon", ico: "💜", name: "Неон", desc: "Свечение и пульс", badge: "Loop", params: [{ key: "spd", label: "Скорость", min: 5, max: 30, step: 1 }] },
  { key: "typewriter", ico: "⌨️", name: "Печатная", desc: "Появление буквами", badge: "×1", params: [{ key: "spd", label: "Скорость", min: 5, max: 50, step: 1 }] },
];

const editorFxDefaults = {
  heartbeat: { spd: 1.4, sc: 1.18 },
  spin: { spd: 0.7 },
  spinloop: { spd: 2.0 },
  float: { spd: 2.4, d: 8 },
  bounce: { spd: 1.2, d: 16 },
  shake: { spd: 0.5, d: 6 },
  swing: { spd: 1.0, ang: 14 },
  pulse: { spd: 1.6, sc: 0.3 },
  zoom: { spd: 2.0, sc: 1.25 },
  jelly: { spd: 0.8 },
  glitch: { spd: 1.5 },
  neon: { spd: 1.5 },
  typewriter: { spd: 2.0 },
};

function shortStoryLabel(label) {
  const txt = safeText(label || "");
  if (txt.length <= 8) return txt;
  return `${txt.slice(0, 8)}…`;
}

function parseBool(v) {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  if (typeof v === "string") {
    const s = safeText(v).toLowerCase();
    if (!s) return false;
    if (["true", "1", "yes", "y", "on", "да"].includes(s)) return true;
    if (["false", "0", "no", "n", "off", "нет"].includes(s)) return false;
  }
  return false;
}

function parseTimeMs(v) {
  const raw = safeText(v);
  if (!raw) return NaN;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : NaN;
}

function normalizeStoryBackground(v, idx) {
  const raw = safeText(v);
  if (!raw) return storyBackgroundByIndex(idx);
  if (raw.includes("gradient(") || raw.startsWith("#") || raw.startsWith("rgb")) return raw;
  if (/^https?:\/\//i.test(raw) || raw.startsWith("/")) {
    const url = resolveUrl(raw);
    return `linear-gradient(180deg, rgba(10,10,14,0.18), rgba(10,10,14,0.42)), url(${url})`;
  }
  return storyBackgroundByIndex(idx);
}

function inferStoryTarget(raw) {
  const explicit = safeText(raw?.targetTab || raw?.tab || raw?.target).toLowerCase();
  if (["blog", "tests", "forum", "reviews", "certs"].includes(explicit)) return explicit;

  const source = safeText(raw?.label || raw?.title || raw?.name).toLowerCase();
  if (!source) return "";
  if (source.includes("тест")) return "tests";
  if (source.includes("форум") || source.includes("faq") || source.includes("вопрос")) return "forum";
  if (source.includes("отзыв")) return "reviews";
  if (source.includes("сертифик") || source.includes("диплом")) return "certs";
  if (source.includes("подкаст") || source.includes("блог") || source.includes("литератур")) return "blog";
  return "";
}

function parseStoryEditorPayload(rawStory) {
  if (!rawStory || typeof rawStory !== "object") return null;
  const directCandidate =
    rawStory.editorPayload ??
    rawStory.editor_payload ??
    rawStory.payload ??
    null;
  const direct = directCandidate && typeof directCandidate === "object" ? directCandidate : null;
  let parsed = direct;
  if (!parsed) {
    const rawJson = safeText(
      rawStory.editorPayloadJson ||
      rawStory.editor_payload_json ||
      (typeof rawStory.editorPayload === "string" ? rawStory.editorPayload : "") ||
      (typeof rawStory.editor_payload === "string" ? rawStory.editor_payload : "") ||
      (typeof rawStory.payload === "string" ? rawStory.payload : "") ||
      ""
    );
    if (rawJson) {
      try {
        const json = JSON.parse(rawJson);
        if (json && typeof json === "object") parsed = json;
      } catch {
        parsed = null;
      }
    }
  }
  if (!parsed || typeof parsed !== "object") return null;

  const mediaTypeRaw = safeText(parsed?.media?.type || "").toLowerCase();
  const mediaType = mediaTypeRaw === "video" ? "video" : mediaTypeRaw === "image" ? "image" : "";
  const mediaUrlRaw = safeText(parsed?.media?.url || "");
  const mediaUrl = mediaUrlRaw ? resolveUrl(mediaUrlRaw) : "";
  const bgImageRaw = safeText(parsed?.backgroundImageUrl || parsed?.bgImageUrl || parsed?.backgroundImage || "");
  const backgroundImageUrl = bgImageRaw ? resolveUrl(bgImageRaw) : "";

  const elements = toArray(parsed?.elements)
    .filter((item) => item && typeof item === "object")
    .map((item, idx) => {
      const type = safeText(item.type || "text").toLowerCase();
      const base = {
        ...item,
        id: Number.isFinite(Number(item.id)) ? Number(item.id) : idx + 1,
        type,
        x: Number(item.x || 0),
        y: Number(item.y || 0),
        rot: Number(item.rot || 0),
      };
      if (type === "image") {
        const imgData = { ...(item.imgData || {}) };
        const imgUrlRaw = safeText(imgData.url || item.url || "");
        return {
          ...base,
          w: Number(item.w || 180),
          fxParams: item.fxParams && typeof item.fxParams === "object"
            ? {
                ...item.fxParams,
                spd: Number(item.fxParams.spd ?? item.fxParams.speed ?? 0) || undefined,
                sc: Number(item.fxParams.sc ?? item.fxParams.scale ?? 0) || undefined,
                d: Number(item.fxParams.d ?? item.fxParams.distance ?? 0) || undefined,
                ang: Number(item.fxParams.ang ?? item.fxParams.angle ?? 0) || undefined,
              }
            : {},
          imgData: { ...imgData, url: imgUrlRaw ? resolveUrl(imgUrlRaw) : "" },
        };
      }
      if (type === "sticker") {
        return { ...base, emoji: safeText(item.emoji || "✨") };
      }
      return {
        ...base,
        type: "text",
        text: safeText(item.text || ""),
        font: safeText(item.font || item.textData?.ff || "Onest, sans-serif"),
        color: safeText(item.color || item.textData?.color || "#ffffff"),
        size: Number(item.size || item.textData?.sz || 24),
        fxParams: item.fxParams && typeof item.fxParams === "object"
          ? {
              ...item.fxParams,
              spd: Number(item.fxParams.spd ?? item.fxParams.speed ?? 0) || undefined,
              sc: Number(item.fxParams.sc ?? item.fxParams.scale ?? 0) || undefined,
              d: Number(item.fxParams.d ?? item.fxParams.distance ?? 0) || undefined,
              ang: Number(item.fxParams.ang ?? item.fxParams.angle ?? 0) || undefined,
            }
          : {},
        textData: item.textData && typeof item.textData === "object" ? item.textData : null,
      };
    });

  return {
    ...parsed,
    label: safeText(parsed.label || ""),
    background: safeText(parsed.background || ""),
    backgroundImageUrl,
    media: mediaType && mediaUrl ? { type: mediaType, url: mediaUrl } : null,
    elements,
  };
}

function mapStoryItem(raw, idx, fallbackName) {
  if (typeof raw === "string") {
    return {
      id: `story-${idx}`,
      label: `История ${idx + 1}`,
      title: safeText(raw),
      text: "Короткая мысль от психолога.",
      emoji: "✨",
      ctaLabel: "Записаться",
      isNew: idx === 0,
      timeLabel: "сегодня",
      background: storyBackgroundByIndex(idx),
      author: fallbackName,
      targetTab: "",
      actionUrl: "",
      seenByMe: false,
      seenAt: "",
      myReaction: "",
      reactionsCount: 0,
      repliesCount: 0,
      saved: false,
      expiresAt: "",
      mine: false,
      sortOrder: idx,
      mediaType: "",
      mediaUrl: "",
    };
  }

  const title = safeText(raw?.title || raw?.name || raw?.label || `История ${idx + 1}`);
  const text = safeText(raw?.text || raw?.description || raw?.body || raw?.subtitle || "");
  const timeLabel =
    safeText(
      raw?.timeLabel ||
        raw?.time ||
        formatDateRu(raw?.updatedAt || raw?.createdAt || raw?.publishedAt, { withYear: false })
    ) || "сегодня";
  const seenAt = safeText(raw?.seenAt || raw?.seen_at || "");
  const updatedAt = safeText(raw?.updatedAt || raw?.updated_at || raw?.createdAt || raw?.created_at || "");
  const seenByMeRaw = parseBool(raw?.seenByMe ?? raw?.seen_by_me);
  const seenAtMs = parseTimeMs(seenAt);
  const updatedAtMs = parseTimeMs(updatedAt);
  const staleSeenMark =
    Number.isFinite(updatedAtMs) &&
    Number.isFinite(seenAtMs) &&
    updatedAtMs > seenAtMs + 1000;
  const seenByTimestamp = Number.isFinite(seenAtMs);
  const seenByMe = staleSeenMark ? false : (seenByMeRaw || seenByTimestamp);
  const editorPayload = parseStoryEditorPayload(raw);
  const payloadMediaType = safeText(editorPayload?.media?.type || "").toLowerCase();
  const payloadMediaUrl = safeText(editorPayload?.media?.url || "");
  const payloadBgImageUrl = safeText(editorPayload?.backgroundImageUrl || "");

  return {
    id: raw?.id ?? `story-${idx}`,
    label: safeText(raw?.label || editorPayload?.label || title || `История ${idx + 1}`),
    title,
    text,
    emoji: safeText(raw?.emoji || raw?.icon || "✨"),
    ctaLabel: safeText(raw?.ctaLabel || raw?.cta || "Записаться"),
    isNew:
      typeof raw?.isNew === "boolean" || typeof raw?.is_new === "boolean"
        ? Boolean(raw?.isNew || raw?.is_new)
        : !seenByMe,
    timeLabel,
    background: normalizeStoryBackground(
      raw?.background ||
        raw?.bg ||
        raw?.backgroundColor ||
        editorPayload?.background ||
        payloadBgImageUrl ||
        raw?.coverImageUrl ||
        raw?.image,
      idx
    ),
    author: safeText(raw?.author || raw?.authorName || fallbackName),
    targetTab: inferStoryTarget(raw),
    actionUrl: safeText(raw?.actionUrl || raw?.url || ""),
    seenByMe,
    seenAt: staleSeenMark ? "" : seenAt,
    myReaction: safeText(raw?.myReaction || raw?.reaction || ""),
    reactionsCount: Number(raw?.reactionsCount ?? raw?.reactions ?? 0) || 0,
    repliesCount: Number(raw?.repliesCount ?? raw?.replies ?? 0) || 0,
    saved: Boolean(raw?.saved || raw?.isSaved || raw?.pinned || raw?.isPinned),
    expiresAt: safeText(raw?.expiresAt || raw?.expireAt || raw?.expires_at || ""),
    mine: false,
    sortOrder: Number(raw?.sortOrder ?? idx) || idx,
    mediaType: safeText(raw?.mediaType || raw?.media?.type || payloadMediaType || "").toLowerCase(),
    mediaUrl: resolveUrl(
      safeText(raw?.mediaUrl || raw?.media?.url || payloadMediaUrl || raw?.videoUrl || raw?.imageUrl || "")
    ),
    editorPayload,
  };
}

function readStoryExpiryMeta(story, nowMs = Date.now()) {
  if (!story) return { badgeText: "", badgeClass: "", showBar: false, barPercent: 0 };
  if (story.saved) {
    return { badgeText: "📌 Сохранено", badgeClass: "saved", showBar: false, barPercent: 0 };
  }
  const raw = safeText(story.expiresAt);
  if (!raw) return { badgeText: "", badgeClass: "", showBar: false, barPercent: 0 };
  const expiresMs = new Date(raw).getTime();
  if (!Number.isFinite(expiresMs)) return { badgeText: "", badgeClass: "", showBar: false, barPercent: 0 };
  const leftMs = expiresMs - Number(nowMs || Date.now());
  if (leftMs <= 0) {
    return { badgeText: "⌛ Истекло", badgeClass: "expired", showBar: false, barPercent: 0 };
  }
  const hours = Math.floor(leftMs / 3600000);
  const mins = Math.floor((leftMs % 3600000) / 60000);
  const shortLeft = hours > 0 ? `${hours}ч` : `${Math.max(1, mins)}м`;
  const barPercent = Math.max(0, Math.min(100, (leftMs / (24 * 3600000)) * 100));
  return {
    badgeText: `⏱ ${shortLeft} до удаления`,
    badgeClass: leftMs < 3 * 3600000 ? "expiring" : "",
    showBar: true,
    barPercent,
  };
}

function safeCount(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return n;
}

function resolveServerStoryId(story) {
  if (!story) return null;
  const numericCandidate = story?.serverId ?? story?.storyId ?? story?.numericId ?? story?.id;
  if (typeof numericCandidate === "number" && Number.isFinite(numericCandidate) && numericCandidate > 0) {
    return numericCandidate;
  }
  const raw = safeText(numericCandidate);
  if (!raw || !/^\d+$/.test(raw)) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function normalizeStoryUrlForApi(value) {
  const raw = safeText(value);
  if (!raw) return null;
  if (raw.startsWith("/") || /^https?:\/\//i.test(raw)) return raw;
  return null;
}

function pickUploadedFileUrl(raw) {
  const payload = unwrapOkPayload(raw);
  const candidates = [
    payload?.file?.url,
    payload?.url,
    payload?.item?.url,
    payload?.item?.file?.url,
    payload?.data?.url,
    payload?.data?.file?.url,
  ];
  return safeText(candidates.find((value) => safeText(value)) || "");
}

export default function PsychologistDetailsView({ psychologist, shareUrl = "", shareTitle = "" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const toast = useToast();

  const isAuthed =
    typeof auth?.isAuthenticated === "boolean"
      ? auth.isAuthenticated
      : Boolean(auth?.isAuthed ?? auth?.token ?? auth?.me);

  const viewerRole = normalizeRole(auth?.me?.role || auth?.role);
  const viewerIsStaff = isStaffRole(viewerRole);

  const userId = psychologist?.userId ?? psychologist?.id;
  const viewerUserId = Number(auth?.me?.id);
  const psychologistUserId = Number(userId);
  const isOwnPsychologistProfile =
    isAuthed &&
    Number.isFinite(viewerUserId) &&
    viewerUserId > 0 &&
    Number.isFinite(psychologistUserId) &&
    psychologistUserId > 0 &&
    viewerUserId === psychologistUserId;

  const profileChatPolicy = React.useMemo(() => resolveChatPolicy(psychologist), [psychologist]);
  const explicitChatAccess = React.useMemo(
    () => resolveExplicitChatAccess(psychologist),
    [psychologist]
  );

  const name = safeText(psychologist?.name || "Психолог");
  const initials = initialsFromName(name);
  const avatarUrl = resolveUrl(psychologist?.avatarUrl || psychologist?.image || "");
  const coverImageUrl = resolveUrl(psychologist?.coverImageUrl || "");
  const bio = safeText(psychologist?.about || psychologist?.description || "");

  const themes = toArray(psychologist?.themes).map((x) => safeText(x)).filter(Boolean);
  const hasPodcast = Boolean(psychologist?.sections?.hasPodcast || safeCount(psychologist?.counts?.podcastsCount) > 0);

  const contacts = React.useMemo(() => normalizeContactPoints(psychologist), [psychologist]);
  const highlights = React.useMemo(() => buildHighlights(psychologist), [psychologist]);
  const canEditStories = isOwnPsychologistProfile;
  const isOwnerProfileView = isOwnPsychologistProfile;
  const storiesInitial = React.useMemo(() => {
    const fromApi = toArray(psychologist?.stories)
      .map((item, idx) => mapStoryItem(item, idx, name))
      .filter((item) => item.title || item.text);
    if (fromApi.length) return fromApi;

    const fallback = highlights
      .slice(0, 8)
      .map((item, idx) => ({
        id: `highlight-story-${item.id || idx}`,
        label: safeText(item.label || `История ${idx + 1}`),
        title: safeText(item.label || `История ${idx + 1}`),
        text: "Короткий формат от психолога: бережный взгляд на ваш запрос.",
        emoji: safeText(item.emoji || "✨"),
        ctaLabel: "Записаться",
        isNew: Boolean(item.isNew || idx === 0),
        timeLabel: "сегодня",
        background: storyBackgroundByIndex(idx),
        author: name,
        mine: isOwnerProfileView,
      }));
    return fallback;
  }, [highlights, isOwnerProfileView, name, psychologist?.stories]);

  const reviewsInitial = React.useMemo(
    () => toArray(psychologist?.reviews).map((r, i) => mapReview(r, i)),
    [psychologist?.reviews]
  );
  const forumInitial = React.useMemo(
    () => toArray(psychologist?.forumAnswers).map((r, i) => mapForumAnswer(r, i)),
    [psychologist?.forumAnswers]
  );
  const blogInitial = React.useMemo(
    () => toArray(psychologist?.blogPosts).map((r, i) => mapBlogPost(r, name, i)),
    [psychologist?.blogPosts, name]
  );
  const testsInitial = React.useMemo(
    () => toArray(psychologist?.tests).map((r, i) => mapTest(r, i)),
    [psychologist?.tests]
  );

  const allCerts = React.useMemo(
    () => toArray(psychologist?.certificates).map((c, i) => mapCertificate(c, i)),
    [psychologist?.certificates]
  );
  const certs = React.useMemo(() => {
    if (viewerIsStaff || isOwnPsychologistProfile) return allCerts;
    return allCerts.filter(isCertificateModerated);
  }, [allCerts, viewerIsStaff, isOwnPsychologistProfile]);
  const education = React.useMemo(
    () => toArray(psychologist?.education).map((e, i) => mapEducation(e, i)),
    [psychologist?.education]
  );

  const [activeTab, setActiveTab] = React.useState("blog");
  const [selectedFormat, setSelectedFormat] = React.useState("individual");
  const [subscribed, setSubscribed] = React.useState(Boolean(psychologist?.isSubscribed));
  const [authRequiredOpen, setAuthRequiredOpen] = React.useState(false);
  const [chatOpenPending, setChatOpenPending] = React.useState(false);
  const [chatOpenRuntimeBlock, setChatOpenRuntimeBlock] = React.useState(null);
  const [shareStatus, setShareStatus] = React.useState("");
  const [storiesFeed, setStoriesFeed] = React.useState(storiesInitial);
  const [storyViewerOpen, setStoryViewerOpen] = React.useState(false);
  const [activeStoryIndex, setActiveStoryIndex] = React.useState(0);
  const [storyPaused, setStoryPaused] = React.useState(false);
  const [storyMenuOpen, setStoryMenuOpen] = React.useState(false);
  const [storyReplyFocused, setStoryReplyFocused] = React.useState(false);
  const [storyNowTick, setStoryNowTick] = React.useState(Date.now());
  const [storyReply, setStoryReply] = React.useState("");
  const [storyEditorOpen, setStoryEditorOpen] = React.useState(false);
  const [storyDrag, setStoryDrag] = React.useState({ dx: 0, dy: 0, active: false });
  const [editorPane, setEditorPane] = React.useState("bg");
  const [editorBgIndex, setEditorBgIndex] = React.useState(0);
  const [editorElements, setEditorElements] = React.useState([]);
  const [editorLabel, setEditorLabel] = React.useState("");
  const [editorTextTitle, setEditorTextTitle] = React.useState("");
  const [editorTextInput, setEditorTextInput] = React.useState("");
  const [editorTextColor, setEditorTextColor] = React.useState("#ffffff");
  const [editorFontFamily, setEditorFontFamily] = React.useState("Manrope, sans-serif");
  const [editorTextStyle, setEditorTextStyle] = React.useState("plain");
  const [editorTextSize, setEditorTextSize] = React.useState(24);
  const [selectedEditorElementId, setSelectedEditorElementId] = React.useState(null);
  const [editorBgImage, setEditorBgImage] = React.useState(null);
  const [editorMedia, setEditorMedia] = React.useState(null);
  const [editorStoryId, setEditorStoryId] = React.useState(null);
  const [cameraOpen, setCameraOpen] = React.useState(false);
  const [cameraFacing, setCameraFacing] = React.useState("user");
  const [isRecording, setIsRecording] = React.useState(false);
  const [recordSeconds, setRecordSeconds] = React.useState(0);
  const [videoBadgeVisible, setVideoBadgeVisible] = React.useState(false);
  const [editorLiveRecordedUrl, setEditorLiveRecordedUrl] = React.useState("");
  const [editorLiveRecordedFile, setEditorLiveRecordedFile] = React.useState(null);
  const [editorExitPreviewFor, setEditorExitPreviewFor] = React.useState(null);

  const [blogPosts, setBlogPosts] = React.useState(blogInitial);
  const [, setTests] = React.useState(testsInitial);
  const [forumAnswers, setForumAnswers] = React.useState(forumInitial);
  const [reviews, setReviews] = React.useState(reviewsInitial);
  const [docModalOpen, setDocModalOpen] = React.useState(false);
  const [activeCertificate, setActiveCertificate] = React.useState(null);

  const [slots, setSlots] = React.useState([]);
  const [slotsLoading, setSlotsLoading] = React.useState(false);
  const [bookingCalendarOpen, setBookingCalendarOpen] = React.useState(false);
  const paymentReturnAutoOpenedRef = React.useRef(false);

  const [loadingByTab, setLoadingByTab] = React.useState({
    blog: false,
    tests: false,
    forum: false,
    reviews: false,
  });

  const [loadedByTab, setLoadedByTab] = React.useState({
    blog: blogInitial.length > 0,
    tests: testsInitial.length > 0,
    forum: forumInitial.length > 0,
    reviews: reviewsInitial.length > 0,
  });

  const effectiveChatPolicy = chatOpenRuntimeBlock?.policy || profileChatPolicy || "";
  const blockedByNobodyPolicy = !viewerIsStaff && effectiveChatPolicy === CHAT_POLICY_NOBODY;
  const blockedByPaidHistoryPolicy =
    !viewerIsStaff &&
    effectiveChatPolicy === CHAT_POLICY_ONLY_CLIENTS_WITH_PAID_HISTORY &&
    explicitChatAccess === false;
  const blockedByRuntimeCheck = !viewerIsStaff && Boolean(chatOpenRuntimeBlock?.blocked);
  const chatButtonHidden = isOwnPsychologistProfile || blockedByNobodyPolicy;
  const chatButtonDisabled =
    chatOpenPending || blockedByPaidHistoryPolicy || blockedByRuntimeCheck;
  const chatButtonHint = blockedByRuntimeCheck
    ? policyRestrictionText(effectiveChatPolicy, safeText(chatOpenRuntimeBlock?.reason))
    : blockedByPaidHistoryPolicy
      ? policyRestrictionText(effectiveChatPolicy)
      : "";

  const storyPointerRef = React.useRef(null);
  const storyHoldTimerRef = React.useRef(null);
  const storySeenPendingRef = React.useRef(new Set());
  const storyCardRef = React.useRef(null);
  const storyActionsBtnRef = React.useRef(null);
  const storyActionsMenuRef = React.useRef(null);
  const storyParticlesLayerRef = React.useRef(null);
  const editorCanvasRef = React.useRef(null);
  const editorBgImageInputRef = React.useRef(null);
  const editorImageInputRef = React.useRef(null);
  const editorVideoInputRef = React.useRef(null);
  const cameraVideoRef = React.useRef(null);
  const editorDragRef = React.useRef(null);
  const editorNextElementIdRef = React.useRef(1);
  const mediaRecorderRef = React.useRef(null);
  const cameraStreamRef = React.useRef(null);
  const recordIntervalRef = React.useRef(null);
  const recordedChunksRef = React.useRef([]);

  React.useEffect(() => {
    setSubscribed(Boolean(psychologist?.isSubscribed));
    setShareStatus("");
    setAuthRequiredOpen(false);
    setChatOpenPending(false);
    setChatOpenRuntimeBlock(null);
    setStoriesFeed(storiesInitial);
    setStoryViewerOpen(false);
    setStoryEditorOpen(false);
    setStoryPaused(false);
    setStoryMenuOpen(false);
    setStoryReplyFocused(false);
    setStoryNowTick(Date.now());
    setStoryDrag({ dx: 0, dy: 0, active: false });
    setStoryReply("");
    setActiveStoryIndex(0);
    setEditorBgIndex(0);
    setEditorElements([]);
    setEditorLabel("");
    setEditorTextInput("");
    setEditorFontFamily("Manrope, sans-serif");
    setEditorTextStyle("plain");
    setEditorTextSize(24);
    setSelectedEditorElementId(null);
    setEditorBgImage(null);
    setEditorMedia(null);
    setEditorStoryId(null);
    setCameraOpen(false);
    setCameraFacing("user");
    setIsRecording(false);
    setRecordSeconds(0);
    setVideoBadgeVisible(false);
    setEditorLiveRecordedUrl("");
    setEditorLiveRecordedFile(null);
    setBookingCalendarOpen(false);
    setBlogPosts(blogInitial);
    setTests(testsInitial);
    setForumAnswers(forumInitial);
    setReviews(reviewsInitial);
    setDocModalOpen(false);
    setActiveCertificate(null);
    setLoadedByTab({
      blog: blogInitial.length > 0,
      tests: testsInitial.length > 0,
      forum: forumInitial.length > 0,
      reviews: reviewsInitial.length > 0,
    });
  }, [psychologist?.userId, psychologist?.id, psychologist?.isSubscribed, blogInitial, testsInitial, forumInitial, reviewsInitial, storiesInitial]);

  React.useEffect(() => {
    let alive = true;
    if (!userId) return undefined;

    const fetchStories = isOwnerProfileView ? psychologistsApi.getMyStories : () => psychologistsApi.getStories(userId);
    fetchStories()
      .then((res) => {
        if (!alive) return;
        const items = extractPageContent(res)
          .map((item, idx) => ({
            ...mapStoryItem(item, idx, name),
            mine: isOwnerProfileView,
          }))
          .filter((item) => item.title || item.text)
          .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0));
        if (isOwnerProfileView) setStoriesFeed(items);
        else if (items.length) setStoriesFeed(items);
      })
      .catch(() => {
        if (!alive) return;
      });

    return () => {
      alive = false;
    };
  }, [isOwnerProfileView, name, userId]);

  const tabs = React.useMemo(() => {
    const sections = psychologist?.sections || {};
    const showCertificatesTab =
      viewerIsStaff || isOwnPsychologistProfile
        ? Boolean(sections?.hasCertificates || allCerts.length)
        : certs.length > 0;
    const items = [
      { key: "blog", label: "Блог", visible: sections?.hasBlog !== false },
      { key: "forum", label: "Форум & ответы", visible: Boolean(sections?.hasForumAnswers || forumInitial.length) },
      { key: "reviews", label: "Отзывы", visible: Boolean(sections?.hasReviews || reviewsInitial.length) },
      { key: "certs", label: "Сертификаты", visible: showCertificatesTab },
    ];
    return items.filter((x) => x.visible);
  }, [
    psychologist?.sections,
    forumInitial.length,
    reviewsInitial.length,
    certs.length,
    allCerts.length,
    viewerIsStaff,
    isOwnPsychologistProfile,
  ]);

  React.useEffect(() => {
    if (!tabs.length) return;
    if (!tabs.some((x) => x.key === activeTab)) setActiveTab(tabs[0].key);
  }, [tabs, activeTab]);

  const triggerBookingBlocked = React.useCallback(() => {
    if (isBookingTemporarilyDisabled()) {
      triggerMaintenanceBanner(toast);
      return true;
    }
    return false;
  }, [toast]);

  const requireAuth = React.useCallback(() => {
    if (isAuthed) return false;
    setAuthRequiredOpen(true);
    return true;
  }, [isAuthed]);

  const bookingOpen = Boolean(psychologist?.bookingEnabled ?? psychologist?.booking?.bookingEnabled)
    && Boolean(psychologist?.acceptingNewClients ?? psychologist?.booking?.acceptingNewClients);

  const handleBook = React.useCallback(() => {
    if (!bookingOpen) {
      toast.info("Запись пока недоступна");
      return;
    }
    if (triggerBookingBlocked()) return;
    if (requireAuth()) return;
    setBookingCalendarOpen(true);
  }, [bookingOpen, requireAuth, toast, triggerBookingBlocked]);

  React.useEffect(() => {
    if (paymentReturnAutoOpenedRef.current) return;
    if (!isAuthed) return;
    const sp = new URLSearchParams(location.search || "");
    if (sp.get("payment") !== "return") return;
    const bookingId = safeText(sp.get("bookingId"));
    if (!bookingId) return;
    paymentReturnAutoOpenedRef.current = true;
    setBookingCalendarOpen(true);
  }, [isAuthed, location.search]);

  const openBlogPost = React.useCallback(
    (post, { comments = false } = {}) => {
      const baseUrl = safeText(post?.url || blogPostPath(post));
      if (!baseUrl) return;
      const clean = baseUrl.split("#")[0];
      navigate(comments ? `${clean}#comments` : clean);
    },
    [navigate]
  );

  const openForumAnswer = React.useCallback(
    (item) => {
      const target = safeText(item?.url);
      if (target) {
        const external = sanitizeLinkHref(target, {
          allowRelative: false,
          allowMailto: false,
          allowTel: false,
          allowHash: false,
        });
        if (external) {
          openExternalUrl(external);
          return;
        }
        const internal = sanitizeLinkHref(target, {
          allowRelative: true,
          allowMailto: false,
          allowTel: false,
          allowHash: false,
        });
        if (internal) {
          navigate(internal);
          return;
        }
      }
      navigate(forumTopicPath(item?.topicId));
    },
    [navigate]
  );

  const handleAskQuestion = () => {
    if (triggerBookingBlocked()) return;
    if (requireAuth()) return;
    toast.info("Раздел вопросов скоро станет доступен для новых запросов.");
  };

  const handleMessage = async () => {
    if (requireAuth()) return;
    if (chatOpenPending) return;
    if (chatButtonDisabled) {
      if (chatButtonHint) toast.info(chatButtonHint);
      return;
    }
    if (!Number.isFinite(psychologistUserId) || psychologistUserId <= 0) {
      navigate("/chat");
      return;
    }

    try {
      setChatOpenPending(true);
      const opened = await chatApi.openPsychologistDialog(psychologistUserId);
      const did = String(opened?.chatId || "").trim();
      if (did) {
        navigate(`/chat?chatId=${encodeURIComponent(did)}`);
        return;
      }
      navigate("/chat");
    } catch (err) {
      const code = extractErrorCode(err);
      if (code === "CHAT_INBOUND_RESTRICTED") {
        const restrictedPolicy =
          normalizeChatPolicy(
            err?.inboundPolicy ??
              err?.directMessagesPolicy ??
              err?.policy ??
              err?.error?.policy
          ) || profileChatPolicy;
        const reasonText = policyRestrictionText(
          restrictedPolicy,
          safeText(err?.message)
        );
        setChatOpenRuntimeBlock({
          blocked: true,
          policy: restrictedPolicy,
          reason: reasonText,
        });
        toast.info(reasonText);
        return;
      }
      toast.error(err?.message || "Не удалось открыть чат");
    } finally {
      setChatOpenPending(false);
    }
  };

  const handleToggleSubscribe = async () => {
    if (requireAuth()) return;
    if (!userId) {
      setSubscribed((prev) => !prev);
      return;
    }
    try {
      const res = await psychologistsApi.toggleSubscribe(userId);
      const next = readBooleanField(res, "isSubscribed", !subscribed);
      setSubscribed(next);
    } catch {
      setSubscribed((prev) => !prev);
    }
  };

  const handleToggleLike = async (postId) => {
    if (requireAuth()) return;
    const current = blogPosts.find((p) => String(p?.id) === String(postId));
    const prevLiked = Boolean(current?.likedByMe);
    const prevLikes = Number(current?.likes || 0);

    try {
      const res = await blogApi.togglePostReaction(postId);
      const payload = unwrapOkPayload(res);
      const parsedLiked = ["likedByMe", "isLiked", "liked", "active"]
        .map((key) => payload?.[key])
        .find((v) => typeof v === "boolean");
      const parsedLikes = Number(payload?.likesCount ?? payload?.reactionsCount ?? payload?.count);

      const nextLiked = typeof parsedLiked === "boolean" ? parsedLiked : !prevLiked;
      const nextLikes = Number.isFinite(parsedLikes)
        ? Math.max(0, parsedLikes)
        : Math.max(0, prevLikes + (nextLiked === prevLiked ? 0 : nextLiked ? 1 : -1));

      setBlogPosts((prev) =>
        prev.map((post) =>
          String(post?.id) === String(postId)
            ? { ...post, likedByMe: nextLiked, likes: nextLikes }
            : post
        )
      );
    } catch (err) {
      toast.error(err?.message || "Не удалось поставить лайк");
    }
  };

  const handleShare = async () => {
    const url = shareUrl || (typeof window !== "undefined" ? window.location.href : "");
    const title = shareTitle || name;
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title, url });
        return;
      }
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setShareStatus("Ссылка скопирована");
        window.setTimeout(() => setShareStatus(""), 2400);
      }
    } catch {
      setShareStatus("Не удалось поделиться");
      window.setTimeout(() => setShareStatus(""), 2400);
    }
  };

  const storyDurationMs = 5200;
  const activeStory = storiesFeed[activeStoryIndex] || null;
  const activeStoryEditorPayload = React.useMemo(
    () => (activeStory?.editorPayload && typeof activeStory.editorPayload === "object" ? activeStory.editorPayload : null),
    [activeStory]
  );
  const activeStoryRenderElements = React.useMemo(
    () =>
      toArray(activeStoryEditorPayload?.elements)
        .filter((item) => item && typeof item === "object")
        .map((item, idx) => ({
          ...item,
          id: Number.isFinite(Number(item.id)) ? Number(item.id) : idx + 1,
          type: safeText(item.type || "text").toLowerCase(),
          x: Number(item.x || 0),
          y: Number(item.y || 0),
          rot: Number(item.rot || 0),
        })),
    [activeStoryEditorPayload]
  );
  const activeStoryHasCanvas = activeStoryRenderElements.length > 0;
  const activeStoryPayloadMedia = activeStoryEditorPayload?.media || null;
  const activeStoryPayloadBgImage = safeText(activeStoryEditorPayload?.backgroundImageUrl || "");
  const activeStoryPayloadBackground = safeText(activeStoryEditorPayload?.background || "");
  const storyPausedEffective = storyPaused || storyMenuOpen || storyReplyFocused;
  const activeStoryExpiry = React.useMemo(
    () => readStoryExpiryMeta(activeStory, storyNowTick),
    [activeStory, storyNowTick]
  );

  const markStorySeenOnServer = React.useCallback(
    async (story, indexHint) => {
      if (!isAuthed) return;
      const storyId = resolveServerStoryId(story);
      if (!userId || !storyId) return;
      if (storySeenPendingRef.current.has(storyId)) return;

      storySeenPendingRef.current.add(storyId);
      try {
        const res = await psychologistsApi.markStorySeen(userId, storyId);
        const nextSeenAt = safeText(unwrapOkPayload(res)?.seenAt || unwrapOkPayload(res)?.seen_at || "");
        setStoriesFeed((prev) => {
          const idx = Number.isFinite(indexHint)
            ? indexHint
            : prev.findIndex((item) => String(item?.id) === String(storyId));
          if (idx < 0 || idx >= prev.length) return prev;
          return prev.map((item, i) =>
            i === idx
              ? { ...item, seenByMe: true, isNew: false, seenAt: nextSeenAt || item.seenAt || "" }
              : item
          );
        });
      } catch {
        // silent fail: seen status is non-critical for core UX
      } finally {
        storySeenPendingRef.current.delete(storyId);
      }
    },
    [isAuthed, userId]
  );

  const clearStoryHoldTimer = React.useCallback(() => {
    if (storyHoldTimerRef.current) {
      window.clearTimeout(storyHoldTimerRef.current);
      storyHoldTimerRef.current = null;
    }
  }, []);

  const stopRecordTimer = React.useCallback(() => {
    if (recordIntervalRef.current) {
      window.clearInterval(recordIntervalRef.current);
      recordIntervalRef.current = null;
    }
  }, []);

  const stopCameraStream = React.useCallback(() => {
    stopRecordTimer();
    const stream = cameraStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }
    setCameraOpen(false);
    setIsRecording(false);
    setRecordSeconds(0);
  }, [stopRecordTimer]);

  const closeStoryViewer = React.useCallback(() => {
    clearStoryHoldTimer();
    setStoryViewerOpen(false);
    setStoryPaused(false);
    setStoryMenuOpen(false);
    setStoryReplyFocused(false);
    setStoryDrag({ dx: 0, dy: 0, active: false });
    setStoryReply("");
    storyPointerRef.current = null;
  }, [clearStoryHoldTimer]);

  const openStoryViewer = React.useCallback((idx) => {
    if (!storiesFeed.length) return;
    const nextIdx = Math.max(0, Math.min(storiesFeed.length - 1, Number(idx) || 0));
    setActiveStoryIndex(nextIdx);
    setStoryPaused(false);
    setStoryMenuOpen(false);
    setStoryReplyFocused(false);
    setStoryDrag({ dx: 0, dy: 0, active: false });
    setStoryReply("");
    setStoriesFeed((prev) =>
      prev.map((item, itemIdx) =>
        itemIdx === nextIdx && item?.isNew ? { ...item, isNew: false, seenByMe: true } : item
      )
    );
    const nextStory = storiesFeed[nextIdx];
    if (nextStory) markStorySeenOnServer(nextStory, nextIdx);
    setStoryViewerOpen(true);
  }, [markStorySeenOnServer, storiesFeed]);

  const goStoryNext = React.useCallback(() => {
    setStoryMenuOpen(false);
    setStoryReplyFocused(false);
    setActiveStoryIndex((prev) => {
      const next = prev + 1;
      if (next >= storiesFeed.length) {
        closeStoryViewer();
        return prev;
      }
      return next;
    });
  }, [closeStoryViewer, storiesFeed.length]);

  const goStoryPrev = React.useCallback(() => {
    setStoryMenuOpen(false);
    setStoryReplyFocused(false);
    setActiveStoryIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const closeStoryEditor = React.useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    stopCameraStream();
    setStoryEditorOpen(false);
    setSelectedEditorElementId(null);
    setEditorStoryId(null);
    setEditorLiveRecordedUrl("");
    setEditorLiveRecordedFile(null);
    setEditorExitPreviewFor(null);
  }, [stopCameraStream]);

  const openStoryEditor = React.useCallback(
    (seedStory = null) => {
      if (!canEditStories) {
        toast.info("Добавлять сторис может только владелец профиля");
        return;
      }
      if (seedStory && typeof seedStory === "object" && "nativeEvent" in seedStory) {
        seedStory = null;
      }
      setEditorPane("bg");
      setEditorBgIndex(0);
      setEditorElements([]);
      setEditorLabel("");
      setEditorTextTitle("");
      setEditorTextInput("");
      setEditorTextColor("#ffffff");
      setEditorFontFamily("Manrope, sans-serif");
      setEditorTextStyle("plain");
      setEditorTextSize(24);
      setSelectedEditorElementId(null);
      setEditorBgImage(null);
      setEditorMedia(null);
      setVideoBadgeVisible(false);
      setEditorLiveRecordedUrl("");
      setEditorLiveRecordedFile(null);
      setEditorExitPreviewFor(null);
      setCameraFacing("user");
      stopCameraStream();
      editorNextElementIdRef.current = 1;
      setEditorStoryId(resolveServerStoryId(seedStory));

      if (seedStory) {
        const seed = mapStoryItem(seedStory, 0, name);
        const payload = seed.editorPayload && typeof seed.editorPayload === "object" ? seed.editorPayload : null;
        if (payload?.background) {
          const bgIdx = editorBackgroundPalette.findIndex((item) => safeText(item) === safeText(payload.background));
          if (bgIdx >= 0) setEditorBgIndex(bgIdx);
        }
        let elements = [];
        if (payload?.elements?.length) {
          elements = payload.elements.map((item, idx) => ({
            ...item,
            id: idx + 1,
            x: Number(item.x || 0),
            y: Number(item.y || 0),
            rot: Number(item.rot || 0),
          }));
        } else {
          if (seed.title) {
            elements.push({
              id: 1,
              type: "text",
              text: seed.title,
              x: 46,
              y: 120,
              font: "Manrope, sans-serif",
              size: 28,
              style: "plain",
              color: "#ffffff",
            });
          }
          if (seed.text) {
            elements.push({
              id: elements.length + 1,
              type: "text",
              text: seed.text,
              x: 34,
              y: 188,
              font: "Manrope, sans-serif",
              size: 20,
              style: "plain",
              color: "#ffffff",
            });
          }
          if (seed.emoji) {
            elements.push({
              id: elements.length + 1,
              type: "sticker",
              emoji: seed.emoji,
              x: 152,
              y: 42,
            });
          }
        }
        setEditorLabel(seed.label || "");
        setEditorTextTitle(seed.title || "");
        setEditorTextInput(seed.text || "");
        if (payload?.backgroundImageUrl) {
          setEditorBgImage({ url: payload.backgroundImageUrl });
        }
        if (payload?.media?.type && payload?.media?.url) {
          setEditorMedia({ type: payload.media.type === "video" ? "video" : "image", url: payload.media.url });
        } else if (seed.mediaType && seed.mediaUrl) {
          setEditorMedia({ type: seed.mediaType === "video" ? "video" : "image", url: seed.mediaUrl });
        }
        if (elements.length) {
          setEditorElements(elements);
          setSelectedEditorElementId(elements[0].id);
          editorNextElementIdRef.current = elements.length + 1;
        }
      }

      setStoryEditorOpen(true);
    },
    [canEditStories, name, stopCameraStream, toast]
  );

  const openEmptyStoryEditor = React.useCallback(() => {
    openStoryEditor(null);
  }, [openStoryEditor]);

  const moveEditorElement = React.useCallback((elementId, clientX, clientY) => {
    const canvas = editorCanvasRef.current;
    const dragState = editorDragRef.current;
    if (!canvas || !dragState) return;
    const canvasRect = canvas.getBoundingClientRect();
    const el = document.getElementById(`se-el-${elementId}`);
    if (!el) return;
    const minX = Math.round(-el.offsetWidth * 0.92);
    const maxX = Math.round(canvas.clientWidth - el.offsetWidth * 0.08);
    const minY = Math.round(-el.offsetHeight * 0.92);
    const maxY = Math.round(canvas.clientHeight - el.offsetHeight * 0.08);
    const x = Math.max(minX, Math.min(clientX - canvasRect.left - dragState.offsetX, maxX));
    const y = Math.max(minY, Math.min(clientY - canvasRect.top - dragState.offsetY, maxY));
    setEditorElements((prev) =>
      prev.map((item) => (item.id === elementId ? { ...item, x, y } : item))
    );
  }, []);

  const updateEditorElement = React.useCallback((id, updater) => {
    if (!id) return;
    setEditorElements((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const patch = typeof updater === "function" ? updater(item) : updater;
        return patch ? { ...item, ...patch } : item;
      })
    );
  }, []);

  const onEditorPointerMove = React.useCallback(
    (event) => {
      const drag = editorDragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      event.preventDefault();
      if (drag.mode === "rotate") {
        const angle = Math.atan2(event.clientY - drag.centerY, event.clientX - drag.centerX) * 180 / Math.PI;
        const nextRot = drag.startRot + (angle - drag.startAngle);
        updateEditorElement(drag.id, { rot: nextRot });
      } else {
        moveEditorElement(drag.id, event.clientX, event.clientY);
      }
    },
    [moveEditorElement, updateEditorElement]
  );

  const stopEditorDrag = React.useCallback(() => {
    editorDragRef.current = null;
    window.removeEventListener("pointermove", onEditorPointerMove);
    window.removeEventListener("pointerup", stopEditorDrag);
    window.removeEventListener("pointercancel", stopEditorDrag);
  }, [onEditorPointerMove]);

  const startEditorDrag = (event, elementId, mode = "move") => {
    if (mode === "move" && event.target?.closest(".se-delete-btn, .se-rotate-btn, .sed-obj-edit")) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const node = document.getElementById(`se-el-${elementId}`);
    if (!node) return;
    const rect = node.getBoundingClientRect();
    setSelectedEditorElementId(elementId);
    const element = editorElements.find((item) => item.id === elementId);
    const startRot = Number(element?.rot || 0);
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const startAngle = Math.atan2(event.clientY - centerY, event.clientX - centerX) * 180 / Math.PI;
    const dragState = {
      id: elementId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      pointerId: event.pointerId,
      startRot,
      centerX,
      centerY,
      startAngle,
      mode: mode === "rotate" ? "rotate" : "move",
    };
    editorDragRef.current = { ...dragState };
    if (typeof node.setPointerCapture === "function") {
      try {
        node.setPointerCapture(event.pointerId);
      } catch {
        // noop
      }
    }
    window.addEventListener("pointermove", onEditorPointerMove, { passive: false });
    window.addEventListener("pointerup", stopEditorDrag);
    window.addEventListener("pointercancel", stopEditorDrag);
  };

  const addTextElement = () => {
    const title = safeText(editorTextTitle);
    const body = safeText(editorTextInput);
    const value = [title, body].filter(Boolean).join("\n");
    if (!value) {
      toast.info("Введите текст");
      return;
    }
    const canvas = editorCanvasRef.current;
    if (!canvas) return;
    const id = editorNextElementIdRef.current++;
    const x = Math.max(0, Math.round(canvas.clientWidth / 2 - 120));
    const y = Math.max(0, Math.round(canvas.clientHeight / 2 - 40));
    setEditorElements((prev) => [
      ...prev,
      {
        id,
        type: "text",
        text: value,
        x,
        y,
        font: editorFontFamily,
        size: editorTextSize,
        style: editorTextStyle,
        color: editorTextColor,
        enter: "none",
        exit: "none",
        fx: null,
        fxParams: {},
        textData: {
          title,
          body,
          ff: editorFontFamily,
          color: editorTextColor,
          sz: editorTextSize,
        },
      },
    ]);
    setSelectedEditorElementId(id);
    setEditorTextTitle("");
    setEditorTextInput("");
  };

  const addStickerElement = (emoji) => {
    const canvas = editorCanvasRef.current;
    if (!canvas) return;
    const id = editorNextElementIdRef.current++;
    const x = Math.max(0, Math.round(canvas.clientWidth / 2 - 30 + (Math.random() - 0.5) * 80));
    const y = Math.max(0, Math.round(canvas.clientHeight / 2 - 30 + (Math.random() - 0.5) * 80));
    setEditorElements((prev) => [...prev, { id, type: "sticker", emoji, x, y, enter: "none", exit: "none", fx: null, fxParams: {} }]);
    setSelectedEditorElementId(id);
  };

  const removeEditorElement = (id) => {
    setEditorElements((prev) => prev.filter((item) => item.id !== id));
    if (selectedEditorElementId === id) setSelectedEditorElementId(null);
  };

  const addImageElementFromFile = React.useCallback((file) => {
    if (!file) return;
    const canvas = editorCanvasRef.current;
    if (!canvas) return;
    const id = editorNextElementIdRef.current++;
    const url = URL.createObjectURL(file);
    const x = Math.max(0, Math.round(canvas.clientWidth / 2 - 110 + (Math.random() - 0.5) * 80));
    const y = Math.max(0, Math.round(canvas.clientHeight / 2 - 110 + (Math.random() - 0.5) * 80));
    const width = Math.max(110, Math.round((canvas.clientWidth * 60) / 100));
    setEditorElements((prev) => [
      ...prev,
      {
        id,
        type: "image",
        x,
        y,
        w: width,
        enter: "none",
        exit: "none",
        fx: null,
        fxParams: {},
        imgData: { url, file, sz: 60, radius: "8px", filter: "none", enter: "none", exit: "none" },
      },
    ]);
    setSelectedEditorElementId(id);
    setEditorPane("img");
    toast.success("Фото добавлено");
  }, [toast]);

  const triggerMediaUpload = (type) => {
    if (type === "image") editorImageInputRef.current?.click();
    else if (type === "bg-image") editorBgImageInputRef.current?.click();
    else editorVideoInputRef.current?.click();
  };

  const handleBgImageUpload = (event) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setEditorBgImage({ url, file });
    event.target.value = "";
    toast.success("Фон загружен");
  };

  const handleImageUpload = (event) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    addImageElementFromFile(file);
    event.target.value = "";
  };

  const handleVideoUpload = (event) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setEditorMedia({ type: "video", url, file });
    setEditorLiveRecordedUrl("");
    setEditorLiveRecordedFile(null);
    setVideoBadgeVisible(true);
    window.setTimeout(() => setVideoBadgeVisible(false), 2500);
    event.target.value = "";
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Камера недоступна в этом браузере");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: cameraFacing },
        audio: true,
      });
      cameraStreamRef.current = stream;
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = stream;
      }
      setEditorLiveRecordedUrl("");
      setCameraOpen(true);
    } catch (err) {
      toast.error(err?.message || "Нет доступа к камере");
    }
  };

  const stopCamera = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      return;
    }
    setEditorLiveRecordedUrl("");
    stopCameraStream();
  };

  const toggleCamera = () => {
    if (cameraOpen) stopCamera();
    else startCamera();
  };

  const flipCamera = async () => {
    const nextFacing = cameraFacing === "user" ? "environment" : "user";
    setCameraFacing(nextFacing);
    if (!cameraOpen) return;
    stopCameraStream();
    window.setTimeout(async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: nextFacing },
          audio: true,
        });
        cameraStreamRef.current = stream;
        if (cameraVideoRef.current) cameraVideoRef.current.srcObject = stream;
        setCameraOpen(true);
      } catch (err) {
        toast.error(err?.message || "Не удалось переключить камеру");
      }
    }, 220);
  };

  const toggleRecord = () => {
    const stream = cameraStreamRef.current;
    if (!stream) return;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      return;
    }
    try {
      recordedChunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data?.size) recordedChunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const chunks = recordedChunksRef.current || [];
        if (chunks.length) {
          const blob = new Blob(chunks, { type: "video/webm" });
          const url = URL.createObjectURL(blob);
          const file = new File([blob], `story-live-${Date.now()}.webm`, { type: "video/webm" });
          setEditorLiveRecordedUrl(url);
          setEditorLiveRecordedFile(file);
          toast.success("Запись готова, нажмите «Использовать»");
        }
        setIsRecording(false);
        stopRecordTimer();
      };
      recorder.start();
      setIsRecording(true);
      setRecordSeconds(0);
      stopRecordTimer();
      recordIntervalRef.current = window.setInterval(() => {
        setRecordSeconds((prev) => {
          const next = prev + 1;
          if (next >= 60 && mediaRecorderRef.current?.state === "recording") {
            mediaRecorderRef.current.stop();
          }
          return next;
        });
      }, 1000);
    } catch (err) {
      toast.error(err?.message || "Запись недоступна");
    }
  };

  const useRecordedLiveVideo = React.useCallback(() => {
    if (!editorLiveRecordedUrl) return;
    setEditorMedia({ type: "video", url: editorLiveRecordedUrl, file: editorLiveRecordedFile });
    setVideoBadgeVisible(true);
    window.setTimeout(() => setVideoBadgeVisible(false), 2500);
    setEditorLiveRecordedUrl("");
    setEditorLiveRecordedFile(null);
    stopCameraStream();
  }, [editorLiveRecordedFile, editorLiveRecordedUrl, stopCameraStream]);

  const uploadStoryMediaFile = React.useCallback(async (file, mediaType = "image") => {
    if (!(file instanceof File)) return null;
    const response =
      mediaType === "video"
        ? await filesApi.uploadAudio(file)
        : await filesApi.uploadImage(file);
    const uploadedUrl = normalizeStoryUrlForApi(pickUploadedFileUrl(response));
    return uploadedUrl || null;
  }, []);

  const prepareEditorPayloadForApi = React.useCallback(async ({ elements = [], media = null, background = "", backgroundImage = null }) => {
    const preparedElements = [];
    for (const raw of Array.isArray(elements) ? elements : []) {
      if (!raw || typeof raw !== "object") continue;
      if (raw.type !== "image") {
        preparedElements.push(raw);
        continue;
      }
      const imgData = { ...(raw.imgData || {}) };
      const rawUrl = safeText(imgData.url);
      let url = normalizeStoryUrlForApi(rawUrl);
      if (!url && rawUrl.startsWith("blob:") && imgData.file instanceof File) {
        url = await uploadStoryMediaFile(imgData.file, "image");
      }
      const nextImgData = { ...imgData, url: url || null };
      delete nextImgData.file;
      preparedElements.push({
        ...raw,
        imgData: nextImgData,
      });
    }

    let preparedMedia = media && typeof media === "object" ? { ...media } : null;
    if (preparedMedia) {
      const rawUrl = safeText(preparedMedia.url);
      let url = normalizeStoryUrlForApi(rawUrl);
      if (!url && rawUrl.startsWith("blob:") && preparedMedia.file instanceof File) {
        url = await uploadStoryMediaFile(preparedMedia.file, preparedMedia.type === "video" ? "video" : "image");
      }
      if (url) {
        preparedMedia.url = url;
      } else {
        preparedMedia = null;
      }
      if (preparedMedia) delete preparedMedia.file;
    }

    let preparedBackgroundImage = backgroundImage && typeof backgroundImage === "object" ? { ...backgroundImage } : null;
    if (preparedBackgroundImage) {
      const rawUrl = safeText(preparedBackgroundImage.url);
      let url = normalizeStoryUrlForApi(rawUrl);
      if (!url && rawUrl.startsWith("blob:") && preparedBackgroundImage.file instanceof File) {
        url = await uploadStoryMediaFile(preparedBackgroundImage.file, "image");
      }
      if (url) {
        preparedBackgroundImage.url = url;
      } else {
        preparedBackgroundImage = null;
      }
      if (preparedBackgroundImage) delete preparedBackgroundImage.file;
    }

    return {
      elements: preparedElements,
      media: preparedMedia,
      background: safeText(background || ""),
      backgroundImageUrl: safeText(preparedBackgroundImage?.url || ""),
    };
  }, [uploadStoryMediaFile]);

  const applyEnterAnimation = React.useCallback((enter) => {
    if (!selectedEditorElementId) return;
    updateEditorElement(selectedEditorElementId, (item) => {
      const next = { enter };
      if (item.type === "image") {
        next.imgData = { ...(item.imgData || {}), enter };
      }
      return next;
    });
  }, [selectedEditorElementId, updateEditorElement]);

  const applyExitAnimation = React.useCallback((exit) => {
    if (!selectedEditorElementId) return;
    updateEditorElement(selectedEditorElementId, (item) => {
      const next = { exit };
      if (item.type === "image") {
        next.imgData = { ...(item.imgData || {}), exit };
      }
      return next;
    });
  }, [selectedEditorElementId, updateEditorElement]);

  const previewExitAnimation = React.useCallback(() => {
    if (!selectedEditorElementId) {
      toast.info("Выберите объект на холсте");
      return;
    }
    const selected = editorElements.find((item) => item.id === selectedEditorElementId);
    const exit = selected?.imgData?.exit || selected?.exit || "none";
    if (!exit || exit === "none") {
      toast.info("Сначала выберите эффект исчезновения");
      return;
    }
    setEditorExitPreviewFor(null);
    window.requestAnimationFrame(() => setEditorExitPreviewFor(selectedEditorElementId));
    window.setTimeout(() => setEditorExitPreviewFor(null), 760);
  }, [editorElements, selectedEditorElementId, toast]);

  const applyFxPreset = React.useCallback((fxKey) => {
    if (!selectedEditorElementId) {
      toast.info("Сначала выберите объект на холсте");
      return;
    }
    updateEditorElement(selectedEditorElementId, {
      fx: fxKey === "none" ? null : fxKey,
      fxParams: fxKey && fxKey !== "none" ? { ...(editorFxDefaults[fxKey] || {}) } : {},
    });
  }, [selectedEditorElementId, toast, updateEditorElement]);

  const setFxParam = React.useCallback((param, rawValue) => {
    if (!selectedEditorElementId) return;
    const selected = editorElements.find((item) => item.id === selectedEditorElementId);
    if (!selected?.fx) return;
    const raw = Number(rawValue);
    let next = raw;
    if (param === "spd") next = raw / 10;
    if (param === "sc") next = raw / 100;
    updateEditorElement(selectedEditorElementId, (item) => ({
      fxParams: { ...(item.fxParams || {}), [param]: next },
    }));
  }, [editorElements, selectedEditorElementId, updateEditorElement]);

  const applySelectedTextEdit = React.useCallback(() => {
    if (!selectedEditorElementId) return;
    const title = safeText(editorTextTitle);
    const body = safeText(editorTextInput);
    const text = [title, body].filter(Boolean).join("\n");
    updateEditorElement(selectedEditorElementId, (item) => {
      if (item.type !== "text") return item;
      return {
        text,
        font: editorFontFamily,
        color: editorTextColor,
        size: editorTextSize,
        textData: {
          title,
          body,
          ff: editorFontFamily,
          color: editorTextColor,
          sz: editorTextSize,
        },
      };
    });
    toast.success("Текст обновлён");
  }, [editorFontFamily, editorTextColor, editorTextInput, editorTextSize, editorTextTitle, selectedEditorElementId, toast, updateEditorElement]);

  const buildStoryUpsertPayload = React.useCallback(
    (storyLike, overrides = {}) => {
      const fallbackLabel = safeText(storyLike?.label || storyLike?.title || editorLabel) || "Новое";
      const title = safeText(storyLike?.title || fallbackLabel) || fallbackLabel;
      const mediaUrl = normalizeStoryUrlForApi(storyLike?.mediaUrl);
      const basePayload = {
        title,
        emoji: safeText(storyLike?.emoji || "✨"),
        description: safeText(storyLike?.text || storyLike?.description || ""),
        coverImageUrl: mediaUrl,
        background: safeText(storyLike?.background || ""),
        ctaLabel: safeText(storyLike?.ctaLabel || "Открыть"),
        actionUrl: normalizeStoryUrlForApi(storyLike?.actionUrl),
        sortOrder: Number.isFinite(Number(storyLike?.sortOrder)) ? Number(storyLike.sortOrder) : 100,
        saved: Boolean(storyLike?.saved),
        expiresAt: safeText(storyLike?.expiresAt || ""),
      };
      if (storyLike?.editorPayload && typeof storyLike.editorPayload === "object") {
        basePayload.editorPayload = storyLike.editorPayload;
      }
      return { ...basePayload, ...overrides };
    },
    [editorLabel]
  );

  const publishStoryDraft = React.useCallback(async () => {
    const textItems = editorElements.filter((item) => item.type === "text");
    const stickerItems = editorElements.filter((item) => item.type === "sticker");
    const fallbackLabel = safeText(editorLabel) || "Новое";
    const draftStory = {
      id: editorStoryId ? String(editorStoryId) : `local-story-${Date.now()}`,
      label: fallbackLabel,
      title: safeText(textItems[0]?.text) || fallbackLabel,
      text: safeText(textItems[1]?.text),
      emoji: safeText(stickerItems[0]?.emoji || "✨"),
      ctaLabel: "Открыть",
      timeLabel: "только что",
      isNew: true,
      background: editorBackgroundPalette[editorBgIndex] || storyBackgroundByIndex(0),
      mediaType: editorMedia?.type || "",
      mediaUrl: editorMedia?.url || "",
      mine: true,
    };

    if (!isOwnerProfileView) {
      const localStory = mapStoryItem(draftStory, 0, name);
      setStoriesFeed((prev) => [localStory, ...prev].slice(0, 24));
      closeStoryEditor();
      toast.success("Сторис опубликован");
      return;
    }

    try {
      const preparedEditor = await prepareEditorPayloadForApi({
        elements: editorElements,
        media:
          draftStory.mediaType && draftStory.mediaUrl
            ? { type: draftStory.mediaType, url: draftStory.mediaUrl, file: editorMedia?.file || null }
            : null,
        background: editorBackgroundPalette[editorBgIndex] || storyBackgroundByIndex(0),
        backgroundImage: editorBgImage?.url ? { url: editorBgImage.url, file: editorBgImage.file || null } : null,
      });
      const effectiveMedia =
        preparedEditor.media ||
        (preparedEditor.backgroundImageUrl ? { type: "image", url: preparedEditor.backgroundImageUrl } : null);
      const payload = buildStoryUpsertPayload(draftStory, {
        coverImageUrl:
          normalizeStoryUrlForApi(effectiveMedia?.url) ||
          normalizeStoryUrlForApi(preparedEditor.backgroundImageUrl) ||
          normalizeStoryUrlForApi(draftStory.mediaUrl) ||
          null,
        background: preparedEditor.background || draftStory.background,
        editorPayload: {
          label: fallbackLabel,
          background: preparedEditor.background || draftStory.background,
          backgroundImageUrl: preparedEditor.backgroundImageUrl || "",
          elements: preparedEditor.elements,
          media: effectiveMedia,
        },
      });
      const res = editorStoryId
        ? await psychologistsApi.updateMyStory(editorStoryId, payload)
        : await psychologistsApi.createMyStory(payload);
      const savedRaw = unwrapOkPayload(res);
      const savedStory = {
        ...mapStoryItem(savedRaw, 0, name),
        mine: true,
      };
      const savedId = resolveServerStoryId(savedStory);
      setStoriesFeed((prev) => {
        if (editorStoryId) {
          return prev.map((item) => (resolveServerStoryId(item) === editorStoryId ? savedStory : item));
        }
        return [{ ...savedStory, id: savedId ?? savedStory.id }, ...prev].slice(0, 24);
      });
      closeStoryEditor();
      toast.success(editorStoryId ? "Сторис обновлена" : "Сторис опубликован");
    } catch (err) {
      toast.error(err?.message || "Не удалось сохранить сторис");
    }
  }, [
    buildStoryUpsertPayload,
    closeStoryEditor,
    editorBgIndex,
    editorElements,
    editorLabel,
    editorBgImage?.file,
    editorBgImage?.url,
    editorMedia?.file,
    editorMedia?.type,
    editorMedia?.url,
    editorStoryId,
    isOwnerProfileView,
    name,
    prepareEditorPayloadForApi,
    toast,
  ]);

  const handleStoryPointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (
      event.target?.closest?.(
        "input,textarea,button,a,.story-tap-left,.story-tap-right,.s-actions-menu,.s-actions-btn"
      )
    ) {
      return;
    }
    if (storyMenuOpen) setStoryMenuOpen(false);
    storyPointerRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      dx: 0,
      dy: 0,
    };
    clearStoryHoldTimer();
    storyHoldTimerRef.current = window.setTimeout(() => {
      setStoryPaused(true);
    }, 150);
  };

  const handleStoryPointerMove = (event) => {
    const ptr = storyPointerRef.current;
    if (!ptr || ptr.pointerId !== event.pointerId) return;
    const dx = event.clientX - ptr.x;
    const dy = event.clientY - ptr.y;
    storyPointerRef.current = { ...ptr, dx, dy };
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) clearStoryHoldTimer();
    setStoryDrag({ dx, dy, active: Math.abs(dx) > 10 || Math.abs(dy) > 10 });
  };

  const finishStoryPointer = (event) => {
    const ptr = storyPointerRef.current;
    if (!ptr || (event && ptr.pointerId !== event.pointerId)) return;
    clearStoryHoldTimer();
    const dx = Number(ptr.dx || 0);
    const dy = Number(ptr.dy || 0);
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);

    if (absY > 120 && dy > 0) {
      closeStoryViewer();
    } else if (absX > 60 && absY < 42) {
      if (dx < 0) goStoryNext();
      else goStoryPrev();
    }

    storyPointerRef.current = null;
    setStoryPaused(false);
    setStoryDrag({ dx: 0, dy: 0, active: false });
  };

  const spawnStoryParticle = React.useCallback((opts = {}) => {
    const layer = storyParticlesLayerRef.current;
    if (!layer) return;
    const particle = document.createElement("div");
    particle.className = "s-particle";
    particle.textContent = String(opts.emoji || "✨");
    particle.style.fontSize = `${Number(opts.size || 24)}px`;
    particle.style.left = `${Number(opts.x || 0)}px`;
    particle.style.top = `${Number(opts.y || 0)}px`;
    particle.style.setProperty("--dx1", opts.dx1 || "0px");
    particle.style.setProperty("--dy1", opts.dy1 || "0px");
    particle.style.setProperty("--dx2", opts.dx2 || "0px");
    particle.style.setProperty("--dy2", opts.dy2 || "0px");
    particle.style.setProperty("--dx3", opts.dx3 || "0px");
    particle.style.setProperty("--dy3", opts.dy3 || "0px");
    particle.style.setProperty("--r", opts.r || "0deg");
    particle.style.setProperty("--r2", opts.r2 || "0deg");
    particle.style.setProperty("--r3", opts.r3 || "0deg");
    particle.style.animation = `${opts.animName || "sp-clap"} ${Number(opts.dur || 1)}s ${opts.easing || "ease-out"} ${Number(opts.delay || 0)}s forwards`;
    layer.appendChild(particle);
    window.setTimeout(() => {
      if (particle.parentNode) particle.remove();
    }, (Number(opts.dur || 1) + Number(opts.delay || 0)) * 1000 + 120);
  }, []);

  const burstHearts = React.useCallback((ox, oy) => {
    const count = 18;
    const emojis = ["❤️", "💕", "💖", "💗", "💓", "❤️", "❤️", "💖"];
    for (let i = 0; i < count; i += 1) {
      const sway1 = (Math.random() - 0.5) * 60;
      const sway2 = (Math.random() - 0.5) * 80;
      const sway3 = (Math.random() - 0.5) * 100;
      const speed = 80 + Math.random() * 120;
      const rise1 = -(speed * 0.3);
      const rise2 = -(speed * 0.7);
      const rise3 = -(speed * 1.2 + 60 + Math.random() * 80);
      const rot = (Math.random() - 0.5) * 30;
      spawnStoryParticle({
        emoji: emojis[i % emojis.length],
        x: ox + (Math.random() - 0.5) * 40,
        y: oy + (Math.random() - 0.5) * 20,
        size: 14 + Math.random() * 18,
        delay: i * 0.04 + Math.random() * 0.08,
        dur: 1.2 + Math.random() * 0.8,
        animName: "sp-heart",
        easing: "ease-out",
        dx1: `${sway1}px`,
        dy1: `${rise1}px`,
        dx2: `${sway1 + sway2}px`,
        dy2: `${rise2}px`,
        dx3: `${sway1 + sway2 + sway3}px`,
        dy3: `${rise3}px`,
        r: `${rot}deg`,
        r2: `${rot + 8}deg`,
        r3: `${rot + 15}deg`,
      });
    }
  }, [spawnStoryParticle]);

  const burstFire = React.useCallback((ox, oy) => {
    const layer = storyParticlesLayerRef.current;
    if (layer) {
      const lw = layer.clientWidth || 1;
      const lh = layer.clientHeight || 1;
      const flash = document.createElement("div");
      flash.className = "s-flash";
      flash.style.setProperty("--fx", `${((ox / lw) * 100).toFixed(1)}%`);
      flash.style.setProperty("--fy", `${((oy / lh) * 100).toFixed(1)}%`);
      layer.appendChild(flash);
      window.setTimeout(() => {
        if (flash.parentNode) flash.remove();
      }, 520);
    }

    const count = 24;
    const emojis = ["🔥", "💥", "✨", "🔥", "🔥", "💥", "🔥", "✨", "🌟"];
    for (let i = 0; i < count; i += 1) {
      const angle = (360 / count) * i + (Math.random() - 0.5) * 15;
      const rad = (angle * Math.PI) / 180;
      const speed1 = 30 + Math.random() * 40;
      const speed2 = 80 + Math.random() * 100;
      const speed3 = 120 + Math.random() * 160;
      spawnStoryParticle({
        emoji: emojis[i % emojis.length],
        x: ox + (Math.random() - 0.5) * 16,
        y: oy + (Math.random() - 0.5) * 16,
        size: 16 + Math.random() * 22,
        delay: Math.random() * 0.06,
        dur: 0.7 + Math.random() * 0.5,
        animName: "sp-fire",
        easing: "ease-out",
        dx1: `${Math.cos(rad) * speed1}px`,
        dy1: `${Math.sin(rad) * speed1}px`,
        dx2: `${Math.cos(rad) * speed2}px`,
        dy2: `${Math.sin(rad) * speed2}px`,
        dx3: `${Math.cos(rad) * speed3}px`,
        dy3: `${Math.sin(rad) * speed3}px`,
        r: `${Math.random() * 360}deg`,
        r2: `${Math.random() * 360}deg`,
        r3: `${Math.random() * 360}deg`,
      });
    }
  }, [spawnStoryParticle]);

  const burstClap = React.useCallback((ox, oy) => {
    const count = 20;
    const emojis = ["👏", "✨", "⭐", "💫", "👏", "👏", "✨"];
    for (let i = 0; i < count; i += 1) {
      const angle = -130 + Math.random() * 180;
      const rad = (angle * Math.PI) / 180;
      const s1 = 40 + Math.random() * 40;
      const s2 = 100 + Math.random() * 80;
      const s3 = 150 + Math.random() * 120;
      spawnStoryParticle({
        emoji: emojis[i % emojis.length],
        x: ox + (Math.random() - 0.5) * 30,
        y: oy + (Math.random() - 0.5) * 20,
        size: 15 + Math.random() * 16,
        delay: i * 0.035 + Math.random() * 0.05,
        dur: 0.9 + Math.random() * 0.6,
        animName: "sp-clap",
        easing: "ease-out",
        dx1: `${Math.cos(rad) * s1}px`,
        dy1: `${Math.sin(rad) * s1}px`,
        dx2: `${Math.cos(rad) * s2}px`,
        dy2: `${Math.sin(rad) * s2}px`,
        dx3: `${Math.cos(rad) * s3}px`,
        dy3: `${Math.sin(rad) * s3}px`,
      });
    }
  }, [spawnStoryParticle]);

  const burstStars = React.useCallback((ox, oy) => {
    const count = 28;
    const emojis = ["🤩", "⭐", "✨", "🌟", "💫", "⭐", "✨", "🤩", "🌠"];
    for (let i = 0; i < count; i += 1) {
      const angle = (360 / count) * i + (Math.random() - 0.5) * 10;
      const rad = (angle * Math.PI) / 180;
      const s1 = 20 + Math.random() * 20;
      const s2 = 70 + Math.random() * 80;
      const s3 = 130 + Math.random() * 120;
      spawnStoryParticle({
        emoji: emojis[i % emojis.length],
        x: ox + (Math.random() - 0.5) * 10,
        y: oy + (Math.random() - 0.5) * 10,
        size: 13 + Math.random() * 18,
        delay: Math.random() * 0.05,
        dur: 1.1 + Math.random() * 0.5,
        animName: "sp-fire",
        easing: "cubic-bezier(.22,1,.36,1)",
        dx1: `${Math.cos(rad) * s1}px`,
        dy1: `${Math.sin(rad) * s1}px`,
        dx2: `${Math.cos(rad) * s2}px`,
        dy2: `${Math.sin(rad) * s2}px`,
        dx3: `${Math.cos(rad) * s3}px`,
        dy3: `${Math.sin(rad) * s3}px`,
        r: `${i * 30}deg`,
        r2: `${i * 50}deg`,
        r3: `${i * 70}deg`,
      });
    }
  }, [spawnStoryParticle]);

  const burstGeneric = React.useCallback((emoji, ox, oy, count = 16) => {
    for (let i = 0; i < count; i += 1) {
      const angle = Math.random() * 360;
      const rad = (angle * Math.PI) / 180;
      const s1 = 20 + Math.random() * 30;
      const s2 = 70 + Math.random() * 70;
      const s3 = 130 + Math.random() * 90;
      spawnStoryParticle({
        emoji,
        x: ox + (Math.random() - 0.5) * 20,
        y: oy + (Math.random() - 0.5) * 20,
        size: 14 + Math.random() * 14,
        delay: Math.random() * 0.1,
        dur: 0.9 + Math.random() * 0.6,
        animName: "sp-clap",
        easing: "ease-out",
        dx1: `${Math.cos(rad) * s1}px`,
        dy1: `${Math.sin(rad) * s1}px`,
        dx2: `${Math.cos(rad) * s2}px`,
        dy2: `${Math.sin(rad) * s2}px`,
        dx3: `${Math.cos(rad) * s3}px`,
        dy3: `${Math.sin(rad) * s3}px`,
      });
    }
  }, [spawnStoryParticle]);

  const handleStoryReaction = async (emoji, event) => {
    const rect = event?.currentTarget?.getBoundingClientRect?.();
    const btnNode = event?.currentTarget;
    if (btnNode) {
      btnNode.style.transform = "scale(1.4)";
      btnNode.style.background = "rgba(255,255,255,.35)";
      window.setTimeout(() => {
        btnNode.style.transform = "";
        btnNode.style.background = "";
      }, 300);
    }

    const layerRect = storyParticlesLayerRef.current?.getBoundingClientRect?.();
    if (rect && layerRect) {
      const ox = rect.left + rect.width / 2 - layerRect.left;
      const oy = rect.top + rect.height / 2 - layerRect.top;
      if (emoji === "❤️") burstHearts(ox, oy);
      else if (emoji === "🔥") burstFire(ox, oy);
      else if (emoji === "👏") burstClap(ox, oy);
      else if (emoji === "🤩") burstStars(ox, oy);
      else burstGeneric(emoji, ox, oy, 18);
    }

    const storyId = resolveServerStoryId(activeStory);
    if (!isAuthed || !userId || !storyId) return;
    try {
      const res = await psychologistsApi.reactStory(userId, storyId, emoji);
      const item = unwrapOkPayload(res);
      const serverReaction = safeText(item?.myReaction || item?.reaction || emoji) || emoji;
      const serverCount = readCountField(item, "reactionsCount", NaN);

      setStoriesFeed((prev) =>
        prev.map((story, idx) => {
          if (idx !== activeStoryIndex) return story;
          const hadReaction = Boolean(story?.myReaction);
          const nextCount = Number.isFinite(serverCount)
            ? serverCount
            : hadReaction
              ? Number(story?.reactionsCount || 0)
              : Number(story?.reactionsCount || 0) + 1;
          return {
            ...story,
            myReaction: serverReaction,
            reactionsCount: nextCount,
          };
        })
      );
    } catch (err) {
      toast.error(err?.message || "Не удалось отправить реакцию");
    }
  };

  const handleStoryAction = (story) => {
    const targetTab = safeText(story?.targetTab || "").toLowerCase();
    if (targetTab && tabs.some((tab) => tab.key === targetTab)) {
      setActiveTab(targetTab);
      closeStoryViewer();
      return;
    }
    const actionUrl = safeText(story?.actionUrl || "");
    if (actionUrl) {
      const external = sanitizeLinkHref(actionUrl, { allowRelative: false, allowMailto: false, allowTel: false, allowHash: false });
      if (external) {
        openExternalUrl(external);
        return;
      }
      const internal = sanitizeLinkHref(actionUrl, { allowRelative: true, allowMailto: false, allowTel: false, allowHash: false });
      if (internal) {
        navigate(internal);
        return;
      }
    }
    handleBook();
  };

  const closeStoryMenu = React.useCallback(() => {
    setStoryMenuOpen(false);
  }, []);

  const toggleStoryMenu = React.useCallback((event) => {
    event?.stopPropagation?.();
    setStoryMenuOpen((prev) => !prev);
  }, []);

  const copyStoryLink = React.useCallback(async () => {
    const base =
      shareUrl || (typeof window !== "undefined" ? window.location.origin + window.location.pathname : "");
    const storyId = safeText(activeStory?.id || "");
    const url = storyId ? `${base}?story=${encodeURIComponent(storyId)}` : base;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success("Ссылка на сторис скопирована");
      } else {
        toast.info("Не удалось скопировать ссылку");
      }
    } catch {
      toast.info("Не удалось скопировать ссылку");
    } finally {
      closeStoryMenu();
    }
  }, [activeStory?.id, closeStoryMenu, shareUrl, toast]);

  const shareStory = React.useCallback(async () => {
    const base =
      shareUrl || (typeof window !== "undefined" ? window.location.origin + window.location.pathname : "");
    const storyId = safeText(activeStory?.id || "");
    const url = storyId ? `${base}?story=${encodeURIComponent(storyId)}` : base;
    try {
      if (navigator.share) {
        await navigator.share({ title: activeStory?.title || name, url });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success("Ссылка скопирована для отправки");
      } else {
        toast.info("Поделиться не поддерживается в этом браузере");
      }
    } catch {
      // user cancelled or browser blocked
    } finally {
      closeStoryMenu();
    }
  }, [activeStory?.id, activeStory?.title, closeStoryMenu, name, shareUrl, toast]);

  const reportStory = React.useCallback(() => {
    closeStoryMenu();
    toast.info("Жалоба отправлена, модерация проверит сторис");
  }, [closeStoryMenu, toast]);

  const toggleSaveStory = React.useCallback(async () => {
    if (!activeStory) return;
    const serverId = resolveServerStoryId(activeStory);
    const nextSaved = !Boolean(activeStory.saved);
    const localPatched = {
      ...activeStory,
      saved: nextSaved,
      expiresAt: nextSaved ? "" : activeStory.expiresAt,
    };

    if (isOwnerProfileView && serverId) {
      try {
        const payload = buildStoryUpsertPayload(activeStory, {
          saved: nextSaved,
          expiresAt: nextSaved ? null : activeStory.expiresAt,
        });
        const res = await psychologistsApi.updateMyStory(serverId, payload);
        const updated = { ...mapStoryItem(unwrapOkPayload(res), activeStoryIndex, name), mine: true };
        setStoriesFeed((prev) => prev.map((item, idx) => (idx === activeStoryIndex ? updated : item)));
      } catch (err) {
        toast.error(err?.message || "Не удалось обновить сторис");
        closeStoryMenu();
        return;
      }
    } else {
      setStoriesFeed((prev) => prev.map((item, idx) => (idx === activeStoryIndex ? localPatched : item)));
    }

    closeStoryMenu();
    toast.success(nextSaved ? "Сохранено навсегда" : "Убрано из сохраненных");
  }, [activeStory, activeStoryIndex, buildStoryUpsertPayload, closeStoryMenu, isOwnerProfileView, name, toast]);

  const setStoryExpiry = React.useCallback(
    async (hours = 24) => {
      if (!activeStory) return;
      const nextIso = new Date(Date.now() + Number(hours || 24) * 3600000).toISOString();
      const serverId = resolveServerStoryId(activeStory);

      if (isOwnerProfileView && serverId) {
        try {
          const payload = buildStoryUpsertPayload(activeStory, {
            saved: false,
            expiresAt: nextIso,
          });
          const res = await psychologistsApi.updateMyStory(serverId, payload);
          const updated = { ...mapStoryItem(unwrapOkPayload(res), activeStoryIndex, name), mine: true };
          setStoriesFeed((prev) => prev.map((item, idx) => (idx === activeStoryIndex ? updated : item)));
        } catch (err) {
          toast.error(err?.message || "Не удалось обновить срок сторис");
          closeStoryMenu();
          return;
        }
      } else {
        setStoriesFeed((prev) =>
          prev.map((item, idx) =>
            idx === activeStoryIndex
              ? {
                  ...item,
                  saved: false,
                  expiresAt: nextIso,
                }
              : item
          )
        );
      }
      closeStoryMenu();
      toast.success(`Сторис будет удалена через ${hours} ч`);
    },
    [activeStory, activeStoryIndex, buildStoryUpsertPayload, closeStoryMenu, isOwnerProfileView, name, toast]
  );

  const deleteStory = React.useCallback(async () => {
    if (!activeStory) return;
    const ok = typeof window === "undefined" ? true : window.confirm("Удалить эту сторис?");
    if (!ok) return;
    const serverId = resolveServerStoryId(activeStory);
    if (isOwnerProfileView && serverId) {
      try {
        await psychologistsApi.deleteMyStory(serverId);
      } catch (err) {
        toast.error(err?.message || "Не удалось удалить сторис");
        closeStoryMenu();
        return;
      }
    }
    setStoriesFeed((prev) => prev.filter((_, idx) => idx !== activeStoryIndex));
    closeStoryMenu();
    toast.success("Сторис удалена");
  }, [activeStory, activeStoryIndex, closeStoryMenu, isOwnerProfileView, toast]);

  const editCurrentStory = React.useCallback(() => {
    if (!activeStory) return;
    closeStoryMenu();
    closeStoryViewer();
    window.setTimeout(() => openStoryEditor(activeStory), 60);
  }, [activeStory, closeStoryMenu, closeStoryViewer, openStoryEditor]);

  const submitStoryReply = React.useCallback(async () => {
    if (!safeText(storyReply)) {
      toast.info("Введите сообщение");
      return;
    }
    const msg = safeText(storyReply);

    try {
      const storyId = resolveServerStoryId(activeStory);
      if (isAuthed && userId && storyId) {
        const res = await psychologistsApi.replyStory(userId, storyId, msg);
        const item = unwrapOkPayload(res);
        const repliesCount = readCountField(item, "repliesCount", NaN);
        setStoriesFeed((prev) =>
          prev.map((story, idx) => {
            if (idx !== activeStoryIndex) return story;
            return {
              ...story,
              repliesCount: Number.isFinite(repliesCount)
                ? repliesCount
                : Number(story?.repliesCount || 0) + 1,
            };
          })
        );
      } else {
        setStoriesFeed((prev) =>
          prev.map((story, idx) => {
            if (idx !== activeStoryIndex) return story;
            return {
              ...story,
              repliesCount: Number(story?.repliesCount || 0) + 1,
            };
          })
        );
      }
      toast.success("Ответ отправлен");
      setStoryReply("");
    } catch (err) {
      toast.error(err?.message || "Не удалось отправить ответ");
    }
  }, [activeStory, activeStoryIndex, isAuthed, storyReply, toast, userId]);

  React.useEffect(() => {
    if (!storyViewerOpen || storyPausedEffective || !storiesFeed.length) return undefined;
    const timer = window.setTimeout(() => {
      goStoryNext();
    }, storyDurationMs);
    return () => window.clearTimeout(timer);
  }, [activeStoryIndex, goStoryNext, storyPausedEffective, storyViewerOpen, storiesFeed.length]);

  React.useEffect(() => {
    if (!storyViewerOpen || !safeText(activeStory?.expiresAt)) return undefined;
    setStoryNowTick(Date.now());
    const interval = window.setInterval(() => {
      setStoryNowTick(Date.now());
    }, 30000);
    return () => window.clearInterval(interval);
  }, [activeStory?.expiresAt, storyViewerOpen]);

  React.useEffect(() => {
    if (!storyViewerOpen || !storyMenuOpen) return undefined;
    const onOutside = (event) => {
      const menuNode = storyActionsMenuRef.current;
      const btnNode = storyActionsBtnRef.current;
      const target = event.target;
      if (menuNode?.contains(target) || btnNode?.contains(target)) return;
      setStoryMenuOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, [storyMenuOpen, storyViewerOpen]);

  React.useEffect(() => {
    if (!storyViewerOpen) return;
    setStoryMenuOpen(false);
  }, [activeStoryIndex, storyViewerOpen]);

  React.useEffect(() => {
    if (storiesFeed.length) {
      if (activeStoryIndex >= storiesFeed.length) {
        setActiveStoryIndex(storiesFeed.length - 1);
      }
      return;
    }
    if (storyViewerOpen) closeStoryViewer();
  }, [activeStoryIndex, closeStoryViewer, storiesFeed.length, storyViewerOpen]);

  React.useEffect(() => {
    if (!storyViewerOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeStoryViewer();
      if (e.key === "ArrowRight") goStoryNext();
      if (e.key === "ArrowLeft") goStoryPrev();
      if (e.key === "Enter" && document.activeElement?.tagName !== "TEXTAREA") {
        const currentTag = String(document.activeElement?.tagName || "").toLowerCase();
        if (currentTag === "input") {
          e.preventDefault();
          submitStoryReply();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeStoryViewer, goStoryNext, goStoryPrev, storyViewerOpen, submitStoryReply]);

  React.useEffect(() => {
    if (!storyEditorOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeStoryEditor();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeStoryEditor, storyEditorOpen]);

  React.useEffect(() => {
    if (!storyEditorOpen) stopCameraStream();
  }, [stopCameraStream, storyEditorOpen]);

  React.useEffect(() => {
    if (!storyViewerOpen && !storyEditorOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [storyEditorOpen, storyViewerOpen]);

  const selectedEditorObject = editorElements.find((item) => item.id === selectedEditorElementId) || null;

  React.useEffect(() => {
    if (!selectedEditorObject) return;
    if (selectedEditorObject.type === "text") {
      setEditorTextTitle(selectedEditorObject.textData?.title || "");
      setEditorTextInput(selectedEditorObject.textData?.body || "");
      setEditorTextColor(selectedEditorObject.textData?.color || selectedEditorObject.color || "#ffffff");
      setEditorFontFamily(selectedEditorObject.textData?.ff || selectedEditorObject.font || "Onest, sans-serif");
      setEditorTextSize(Number(selectedEditorObject.textData?.sz || selectedEditorObject.size || 24));
    }
  }, [selectedEditorObject]);

  React.useEffect(() => {
    if (!selectedEditorElementId) return;
    updateEditorElement(selectedEditorElementId, (item) => {
      if (item.type !== "text") return item;
      return {
        font: editorFontFamily,
        color: editorTextColor,
        size: editorTextSize,
        textData: {
          ...(item.textData || {}),
          ff: editorFontFamily,
          color: editorTextColor,
          sz: editorTextSize,
        },
      };
    });
  }, [editorFontFamily, editorTextColor, editorTextSize, selectedEditorElementId, updateEditorElement]);

  React.useEffect(() => {
    if (!storyViewerOpen) return;
    setStoriesFeed((prev) => {
      let changed = false;
      const next = prev.map((item, idx) => {
        if (idx === activeStoryIndex && item?.isNew) {
          changed = true;
          return { ...item, seenByMe: true, isNew: false };
        }
        return item;
      });
      return changed ? next : prev;
    });
    if (activeStory) markStorySeenOnServer(activeStory, activeStoryIndex);
  }, [activeStory, activeStoryIndex, markStorySeenOnServer, storyViewerOpen]);

  React.useEffect(
    () => () => {
      clearStoryHoldTimer();
      stopRecordTimer();
      stopCameraStream();
      stopEditorDrag();
    },
    [clearStoryHoldTimer, stopCameraStream, stopEditorDrag, stopRecordTimer]
  );

  const runTabLoad = React.useCallback(
    async (tabKey) => {
      if (!userId) return;
      setLoadingByTab((prev) => ({ ...prev, [tabKey]: true }));
      try {
        if (tabKey === "blog") {
          const res = await psychologistsApi.getBlogPosts(userId, { page: 0, size: 10 });
          setBlogPosts(extractPageContent(res).map((r, i) => mapBlogPost(r, name, i)));
        }
        if (tabKey === "tests") {
          const res = await psychologistsApi.getTests(userId, { page: 0, size: 10 });
          setTests(extractPageContent(res).map((r, i) => mapTest(r, i)));
        }
        if (tabKey === "forum") {
          const res = await psychologistsApi.getForumAnswers(userId, { page: 0, size: 10 });
          setForumAnswers(extractPageContent(res).map((r, i) => mapForumAnswer(r, i)));
        }
        if (tabKey === "reviews") {
          const res = await psychologistsApi.getReviews(userId, { page: 0, size: 10 });
          setReviews(extractPageContent(res).map((r, i) => mapReview(r, i)));
        }
      } catch (err) {
        toast.error(err?.message || "Не удалось загрузить раздел");
      } finally {
        setLoadingByTab((prev) => ({ ...prev, [tabKey]: false }));
        setLoadedByTab((prev) => ({ ...prev, [tabKey]: true }));
      }
    },
    [name, toast, userId]
  );

  React.useEffect(() => {
    if (!activeTab) return;
    if (["blog", "tests", "forum", "reviews"].includes(activeTab) && !loadedByTab[activeTab]) {
      runTabLoad(activeTab);
    }
  }, [activeTab, loadedByTab, runTabLoad]);

  React.useEffect(() => {
    let alive = true;
    if (!userId) return undefined;

    const from = new Date();
    const to = new Date(from);
    to.setDate(to.getDate() + 14);

    const toIso = (d) => d.toISOString().slice(0, 10);
    setSlotsLoading(true);

    const range = { from: toIso(from), to: toIso(to) };

    (async () => {
      try {
        const calendarRes = await psychologistsApi.getCalendar(userId, range);
        let nextSlots = flattenCalendarSlots(calendarRes);

        if (!nextSlots.length) {
          const [schedule, flexibleScheduleRaw, dayOffsRaw, breaksRaw, busyRaw] = await Promise.all([
            sessionsApi.getSchedule(userId).catch(() => null),
            sessionsApi.getFlexibleSchedule(userId, range).catch(() => null),
            sessionsApi.listDayOffs(userId, range).catch(() => []),
            sessionsApi.listWorkBreaks(userId, range).catch(() => []),
            sessionsApi.listCalendar(userId, range).catch(() => []),
          ]);

          const flexibleSchedule =
            flexibleScheduleRaw && Array.isArray(flexibleScheduleRaw?.flexible) && flexibleScheduleRaw.flexible.length
              ? flexibleScheduleRaw
              : null;

          const slotEvents = buildSlotEvents({
            rangeStart: new Date(`${range.from}T00:00:00`),
            rangeEnd: new Date(new Date(`${range.to}T00:00:00`).getTime() + 86400000),
            schedule,
            flexibleSchedule,
            dayOffs: Array.isArray(dayOffsRaw) ? dayOffsRaw : [],
            bookings: Array.isArray(busyRaw) ? busyRaw : [],
            breaks: Array.isArray(breaksRaw) ? breaksRaw : [],
            now: new Date(),
          });

          const nowMs = Date.now();
          nextSlots = Array.from(
            new Set(
              (slotEvents || [])
                .map((event) => safeText(event?.start || ""))
                .filter(Boolean)
            )
          )
            .map((iso) => ({ iso, ms: new Date(iso).getTime() }))
            .filter((item) => Number.isFinite(item.ms) && item.ms >= nowMs)
            .sort((a, b) => a.ms - b.ms)
            .slice(0, 3)
            .map((item) => ({
              iso: item.iso,
              day: formatDaySlot(item.iso),
              time: formatTimeSlot(item.iso),
            }));
        }

        if (!alive) return;
        setSlots(nextSlots);
      } catch {
        if (!alive) return;
        setSlots([]);
      } finally {
        if (alive) setSlotsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [userId]);

  const experienceYears = safeCount(psychologist?.experienceYears);
  const headline = safeText(psychologist?.headline || "");
  const methodChips = toArray(psychologist?.methods)
    .map((item) => safeText(item))
    .filter(Boolean)
    .slice(0, 3);

  const stats = {
    experience: formatYearsPlusRu(experienceYears),
    clients: safeCount(psychologist?.counts?.clientsCount),
    rating: Number(psychologist?.averageRating || 0),
    reviews: safeCount(psychologist?.reviewsCount),
    subscribers: safeCount(psychologist?.counts?.subscribersCount || psychologist?.subscribersCount),
    forumAnswers: safeCount(psychologist?.counts?.forumAnswersCount || psychologist?.forumStats?.answersCount),
  };
  const statCards = [
    { value: stats.experience, label: "опыт" },
    { value: formatCount(stats.clients), label: "клиента" },
    { value: `★ ${(stats.rating || 0).toFixed(1)}`, label: `${formatCount(stats.reviews)} отз.` },
    { value: formatCount(stats.subscribers), label: "подписчика" },
    { value: formatCount(stats.forumAnswers), label: "ответ на форуме" },
  ];

  const distribution = [5, 4, 3, 2, 1].map((stars) => ({
    stars,
    count: safeCount(psychologist?.[`rating${stars}Count`]),
  }));
  const distributionMax = Math.max(...distribution.map((x) => x.count), 1);

  const priceRaw =
    psychologist?.pricePerSession ??
    psychologist?.price_per_session ??
    psychologist?.booking?.pricePerSession ??
    psychologist?.booking?.price_per_session;
  const durationRaw =
    psychologist?.sessionDurationMinutes ??
    psychologist?.session_duration_minutes ??
    psychologist?.sessionDuration ??
    psychologist?.durationMinutes ??
    psychologist?.duration_minutes ??
    psychologist?.booking?.sessionDurationMinutes ??
    psychologist?.booking?.session_duration_minutes ??
    psychologist?.booking?.sessionDuration ??
    psychologist?.booking?.durationMinutes ??
    psychologist?.booking?.duration_minutes;
  const priceValue = Number(priceRaw);
  const durationValue = Number(durationRaw);
  const price = Number.isFinite(priceValue) && priceValue > 0 ? Math.round(priceValue) : null;
  const duration = Number.isFinite(durationValue) && durationValue > 0 ? Math.round(durationValue) : null;
  const priceDisplay = price != null ? moneyParts(price) : null;

  const formatButtons = [
    { key: "individual", label: "Индивидуально", icon: "👤" },
    { key: "group", label: "Группа", icon: "👥" },
  ];
  const calendarPsychologist = React.useMemo(
    () =>
      psychologist
        ? {
            ...psychologist,
            id: userId ?? psychologist.id,
            userId: userId ?? psychologist.userId,
            priceAtTime: price ?? undefined,
            sessionDurationMinutes: duration ?? undefined,
          }
        : null,
    [duration, price, psychologist, userId]
  );
  const storyCardStyle = React.useMemo(() => {
    const dragX = Number(storyDrag.dx || 0);
    const dragY = Number(storyDrag.dy || 0);
    const scale = storyDrag.active
      ? Math.max(0.92, 1 - Math.abs(dragY) / 600)
      : 1;
    const opacity = storyDrag.active
      ? Math.max(0.4, 1 - Math.abs(dragY) / 300)
      : 1;

    return {
      transform: `translate3d(${dragX}px, ${dragY}px, 0) scale(${scale})`,
      opacity,
      transition: storyDrag.active ? "none" : "transform 0.22s ease, opacity 0.22s ease",
    };
  }, [storyDrag.active, storyDrag.dx, storyDrag.dy]);
  const editorBackground = editorBackgroundPalette[editorBgIndex] || storyBackgroundByIndex(0);
  const recordTimeLabel = `${Math.floor(recordSeconds / 60)}:${String(recordSeconds % 60).padStart(2, "0")}`;
  const hasCoverImage = Boolean(coverImageUrl);
  const certPreviewUrl = React.useMemo(
    () =>
      sanitizeLinkHref(activeCertificate?.documentUrl || "", {
        allowRelative: true,
        allowMailto: false,
        allowTel: false,
        allowHash: false,
      }),
    [activeCertificate?.documentUrl]
  );
  const certPreviewMediaType = React.useMemo(
    () => detectCertificateMediaType(certPreviewUrl, activeCertificate),
    [certPreviewUrl, activeCertificate]
  );
  const certPreviewIsImage = certPreviewMediaType === "image";
  const certPreviewIsPdf = certPreviewMediaType === "pdf";
  const certPreviewCanEmbedFrame = React.useMemo(
    () => canEmbedDocumentFrame(certPreviewUrl),
    [certPreviewUrl]
  );

  const openCertificateDoc = React.useCallback((cert) => {
    setActiveCertificate(cert || null);
    setDocModalOpen(Boolean(cert));
  }, []);

  const closeCertificateDoc = React.useCallback(() => {
    setDocModalOpen(false);
    setActiveCertificate(null);
  }, []);

  if (!psychologist) return null;

  return (
    <div className="psych-profile-v7">
      <section
        className="cover"
        style={
          hasCoverImage
            ? {
                backgroundImage: `linear-gradient(135deg, rgba(43,43,43,.55), rgba(58,45,94,.35) 45%, rgba(26,74,46,.28) 75%, rgba(90,234,160,.2) 100%), url(${coverImageUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : undefined
        }
      >
        <div className="cover-pattern" />
        <div className="cover-top">
          <div className="cover-badge">
            <div className="cover-badge-dot" />
            {bookingOpen ? "Принимает новых клиентов" : "Запись временно закрыта"}
          </div>
          <button type="button" className="cover-share" onClick={handleShare}>
            ↗ Поделиться
          </button>
        </div>
        <div className="av-wrap">
          <div className="av">
            {avatarUrl ? <img src={avatarUrl} alt={name} /> : initials}
            <div className="av-check">✓</div>
          </div>
        </div>
      </section>

      <section className="profile">
        <div className="profile-top">
          <div>
            <div className="profile-name">{name}</div>
            <div className="profile-chips">
              {headline ? <span className="chip">{headline}</span> : null}
              {methodChips.map((chip) => (
                <span key={`chip-${chip}`} className="chip">
                  {chip}
                </span>
              ))}
              {!headline && !methodChips.length ? <span className="chip">Психолог</span> : null}
            </div>
            <div className="profile-bio">{bio || "Для меня нет «сложных» клиентов — есть вы и ваша история."}</div>
          </div>

          <div className="profile-actions">
            {!chatButtonHidden ? (
              <span title={chatButtonDisabled ? chatButtonHint : ""}>
                <button
                  type="button"
                  className="btn-icon"
                  onClick={handleMessage}
                  aria-label="Написать"
                  disabled={chatButtonDisabled}
                  title={chatButtonDisabled ? chatButtonHint : ""}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            ) : null}
            <button type="button" className="btn-secondary" onClick={handleToggleSubscribe}>
              {subscribed ? "Подписан ✓" : "Подписаться"}
            </button>
            <button type="button" className="btn-primary" onClick={handleBook} disabled={!bookingOpen}>
              Записаться →
            </button>
          </div>
        </div>

        {shareStatus ? <div className="profile-share-status">{shareStatus}</div> : null}

        <div className="profile-tags">
          {themes.map((tag) => (
            <span key={`theme-${tag}`} className="tag tag-m">
              {tag}
            </span>
          ))}
          {psychologist?.workFormatOnline ? <span className="tag tag-l">✓ Онлайн</span> : null}
          {psychologist?.workFormatOffline ? (
            <span className="tag tag-l">✓ Офлайн{psychologist?.regionLabel ? ` · ${psychologist.regionLabel}` : ""}</span>
          ) : null}
          {hasPodcast ? <span className="tag tag-a">♪ Ведёт подкаст</span> : null}
        </div>

        <div className="profile-stats">
          {statCards.map((item) => (
            <div key={item.label} className="stat">
              <div className="stat-n">{item.value}</div>
              <div className="stat-l">{item.label}</div>
            </div>
          ))}
        </div>

        {storiesFeed.length || canEditStories ? (
          <div className="hl-row" id="hlRow">
            {canEditStories ? (
              <div className="hl-item" onClick={openEmptyStoryEditor} role="button" tabIndex={0} onKeyDown={(e) => (e.key === "Enter" ? openEmptyStoryEditor() : null)}>
                <div className="hl-add-ring">＋</div>
                <div className="hl-label">Добавить</div>
              </div>
            ) : null}
            {storiesFeed.map((item, idx) => (
              <div
                key={item.id}
                className="hl-item"
                onClick={() => openStoryViewer(idx)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" ? openStoryViewer(idx) : null)}
              >
                <div className={`hl-ring ${item.seenByMe ? "hl-ring-seen" : ""} ${item.isNew ? "hl-ring-new" : ""}`.trim()}>
                  <div className="hl-thumb" style={{ background: item.background }}>
                    {item.emoji || "✨"}
                  </div>
                  {item.isNew ? <span className="hl-new-dot" /> : null}
                </div>
                <div className="hl-label">{shortStoryLabel(item.label || `История ${idx + 1}`)}</div>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <div className="mobile-booking">
        <div className="mobile-booking-inner">
          <div className="mobile-booking-top">
            <div>
              <div className="mobile-booking-label">Запись на сессию</div>
              <div className="mobile-booking-price">
                {priceDisplay ? `${priceDisplay.num} ${priceDisplay.cur}` : "Цена уточняется"}
                <span>{duration ? `/ ${duration} мин` : ""}</span>
              </div>
            </div>
            <button type="button" className="mobile-booking-btn" onClick={handleBook} disabled={!bookingOpen}>
              Записаться →
            </button>
          </div>
          <div className="bk-fmts">
            {formatButtons.map((fmt) => (
              <button
                key={`mobile-${fmt.key}`}
                type="button"
                className={`bfmt ${selectedFormat === fmt.key ? "on" : ""}`.trim()}
                onClick={() => setSelectedFormat(fmt.key)}
              >
                <span>{fmt.icon}</span> {fmt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="main-layout">
        <aside className="sidebar">
          <div className="card booking-card">
            <div className="bk-label">Запись на сессию</div>
            <div className="bk-price">
              <span className="bk-price-main">{priceDisplay ? `${priceDisplay.num}${priceDisplay.cur}` : "Цена уточняется"}</span>
              <span className="bk-price-per">{duration ? ` /${duration}мин` : ""}</span>
            </div>
            <div className="bk-price-sub">{duration ? `за сессию · ${duration} минут` : "стоимость и длительность уточняются"}</div>
            <div className="bk-fmts">
              {formatButtons.map((fmt) => (
                <button
                  key={fmt.key}
                  type="button"
                  className={`bfmt ${selectedFormat === fmt.key ? "on" : ""}`.trim()}
                  onClick={() => setSelectedFormat(fmt.key)}
                >
                  <span>{fmt.icon}</span> {fmt.label}
                </button>
              ))}
            </div>
            <button type="button" className="btn-book" onClick={handleBook} disabled={!bookingOpen}>
              Выбрать время →
            </button>
            <button type="button" className="btn-msg" onClick={handleAskQuestion}>
              Написать вопрос
            </button>
          </div>

          <div className="card slots-card">
            <div className="card-ttl">Ближайшие слоты</div>
            {!bookingOpen ? <div className="slot-empty">Запись пока недоступна</div> : null}
            {bookingOpen && slotsLoading ? <div className="slot-empty">Загружаем слоты…</div> : null}
            {bookingOpen && !slotsLoading && !slots.length ? <div className="slot-empty">Свободные слоты не найдены</div> : null}
            {bookingOpen && !slotsLoading && slots.map((slot) => (
              <button key={slot.iso} type="button" className="slot" onClick={handleBook}>
                <span className="slot-day">{slot.day}</span>
                <span className="slot-time">{slot.time}</span>
                <div className="slot-dot" />
              </button>
            ))}
            {bookingOpen ? (
              <div className="slots-more" onClick={handleBook} role="button" tabIndex={0} onKeyDown={(e) => (e.key === "Enter" ? handleBook() : null)}>
                Показать все →
              </div>
            ) : null}
          </div>

          <div className="card" id="ratingCard">
            <div className="card-ttl">Рейтинг</div>
            <div className="rating-top">
              <div className="rating-big">{(stats.rating || 0).toFixed(1)}</div>
              <div>
                <div className="stars-lg">{starsText(5)}</div>
                <div className="rating-meta">{stats.reviews} отзывов</div>
              </div>
            </div>
            {distribution.map((row) => {
              const width = `${Math.round((row.count / distributionMax) * 100)}%`;
              return (
                <div key={`star-${row.stars}`} className="rbar">
                  <span className="rbar-n">{row.stars}</span>
                  <div className="rbar-track">
                    <div className="rbar-fill" style={{ width }} />
                  </div>
                  <span className="rbar-c">{row.count}</span>
                </div>
              );
            })}
          </div>

          {education.length ? (
            <div className="card">
              <div className="card-ttl">Образование</div>
              {education.map((edu) => (
                <div key={edu.id} className="edu-row">
                  <div className="edu-ic">{edu.icon}</div>
                  <div>
                    <div className="edu-name">{edu.name}</div>
                    <div className="edu-sub">{edu.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {contacts.length ? (
            <div className="card">
              <div className="card-ttl">Связь</div>
              {contacts.map((contact) => {
                const href = sanitizeLinkHref(contact.url, {
                  allowRelative: false,
                  allowMailto: true,
                  allowTel: true,
                  allowHash: false,
                });
                const displayValue = contactDisplayValue(contact);
                const titleValue = safeText(contact.value || displayValue);

                return (
                  <div key={`${contact.label}-${contact.value}`} className={`ct-row ${href ? "is-link" : ""}`.trim()}>
                    <div className="ct-ic">{contact.type === "email" ? "✉️" : contact.type === "telegram" ? "💬" : contact.type === "location" ? "📍" : "🔗"}</div>
                    <div className="ct-main">
                      <div className="ct-lbl">{contact.label}</div>
                      <div className="ct-val">
                        {href ? (
                          <a
                            href={href}
                            target={/^https?:\/\//i.test(href) ? "_blank" : undefined}
                            rel="noreferrer noopener"
                            title={titleValue}
                          >
                            {displayValue}
                          </a>
                        ) : (
                          <span title={titleValue}>{displayValue}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </aside>

        <div className="main-content">
          <div className="tabs">
            {tabs.map((tab) => (
              <div
                key={tab.key}
                className={`tab ${activeTab === tab.key ? "on" : ""}`.trim()}
                onClick={() => setActiveTab(tab.key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" ? setActiveTab(tab.key) : null)}
              >
                {tab.label}
              </div>
            ))}
          </div>

          {activeTab === "blog" ? (
            <div id="blog" className="sec show">
              {loadingByTab.blog ? <div className="tab-state">Загружаем посты…</div> : null}
              {!loadingByTab.blog && !blogPosts.length ? <div className="tab-state">Посты пока не опубликованы</div> : null}
              {blogPosts.map((post, idx) => {
                const liked = Boolean(post.likedByMe);
                const likesCount = Number(post.likes || 0);
                const canOpenPost = Boolean(safeText(post?.url || blogPostPath(post)));
                return (
                  <article
                    key={post.id}
                    className={`post ${canOpenPost ? "is-clickable" : ""}`.trim()}
                    role={canOpenPost ? "button" : undefined}
                    tabIndex={canOpenPost ? 0 : undefined}
                    onClick={() => (canOpenPost ? openBlogPost(post) : null)}
                    onKeyDown={(event) => {
                      if (!canOpenPost) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openBlogPost(post);
                      }
                    }}
                  >
                    {post.previewImageUrl ? (
                      <div className="post-cover">
                        <img src={post.previewImageUrl} alt={post.title || "Обложка поста"} loading="lazy" decoding="async" />
                        <div className="post-cover-gradient" />
                        <span className={`post-cover-tag ${postTopicClass(post.topicVariant || variantByIndex(idx))}`}>{post.topic}</span>
                      </div>
                    ) : null}
                    <div className="post-body">
                      <div className="post-meta">
                        <div className="post-av">{initialsFromName(post.author || name)}</div>
                        <div>
                          <div className="post-nm">{post.author || name}</div>
                          <div className="post-dt">{post.date || ""}</div>
                        </div>
                      </div>
                      {!post.previewImageUrl ? <span className={`post-topic ${postTopicClass(post.topicVariant || variantByIndex(idx))}`}>{post.topic}</span> : null}
                      {post.title ? <div className="post-title">{post.title}</div> : null}
                      <div className="post-text">{post.text || "Описание поста недоступно"}</div>
                      <div className="post-foot">
                        <button
                          type="button"
                          className={`pa ${liked ? "liked" : ""}`.trim()}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleToggleLike(post.id);
                          }}
                        >
                          <span>{liked ? "♥" : "♡"}</span> <span className="cnt">{likesCount}</span>
                        </button>
                        <button
                          type="button"
                          className="pa"
                          onClick={(event) => {
                            event.stopPropagation();
                            openBlogPost(post, { comments: true });
                          }}
                        >
                          💬 {post.comments || 0}
                        </button>
                        {bookingOpen ? (
                          <button
                            type="button"
                            className="pa-book"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleBook();
                            }}
                          >
                            Записаться
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : null}

          {activeTab === "forum" ? (
            <div id="forum" className="sec show">
              <div className="forum-prompt">
                <div className="forum-prompt-text">Есть вопрос? Спросите психолога — ответы будут доступны в профиле.</div>
                <button type="button" className="btn-ask" onClick={handleAskQuestion}>Задать вопрос</button>
              </div>
              {loadingByTab.forum ? <div className="tab-state">Загружаем ответы…</div> : null}
              {!loadingByTab.forum && !forumAnswers.length ? <div className="tab-state">Пока нет опубликованных ответов</div> : null}
              {forumAnswers.map((item) => {
                const canOpenForum = Boolean(safeText(item?.url) || parsePositiveId(item?.topicId));
                return (
                <article
                  key={item.id}
                  className={`q-card ${canOpenForum ? "is-clickable" : ""}`.trim()}
                  role={canOpenForum ? "button" : undefined}
                  tabIndex={canOpenForum ? 0 : undefined}
                  onClick={() => (canOpenForum ? openForumAnswer(item) : null)}
                  onKeyDown={(event) => {
                    if (!canOpenForum) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openForumAnswer(item);
                    }
                  }}
                >
                  <div className="q-top">
                    <div className="q-title">{item.title}</div>
                    <span className={`q-badge ${item.badgeVariant === "new" ? "q-new" : "q-answered"}`.trim()}>{item.badge}</span>
                  </div>
                  {item.text ? <div className="q-text">{item.text}</div> : null}
                  {item.answer ? (
                    <div className="q-ans">
                      <div className="q-ans-lbl">✓ Ответ психолога</div>
                      <div className="q-ans-text">{item.answer}</div>
                    </div>
                  ) : null}
                  {item.meta ? <div className="q-meta">{item.meta}</div> : null}
                </article>
                );
              })}
            </div>
          ) : null}

          {activeTab === "reviews" ? (
            <div id="reviews" className="sec show">
              {loadingByTab.reviews ? <div className="tab-state">Загружаем отзывы…</div> : null}
              {!loadingByTab.reviews && !reviews.length ? <div className="tab-state">Пока нет отзывов</div> : null}
              {reviews.map((item) => (
                <article key={item.id} className="rev">
                  <div className="rev-head">
                    <span>
                      <div className="rev-nm">{item.name}</div>
                      {item.verified ? <div className="rev-ok">✓ Верифицированный клиент</div> : null}
                    </span>
                    <div className="rev-stars">{starsText(item.stars)}</div>
                  </div>
                  <div className="rev-text">{item.text || ""}</div>
                  <div className="rev-foot">
                    <span>{item.date}</span>
                    <span className="rev-sessions">{item.sessions}</span>
                  </div>
                </article>
              ))}
            </div>
          ) : null}

          {activeTab === "certs" ? (
            <div id="certs" className="sec show">
              <div className="verified-banner">✓ Все дипломы проверены платформой и подтверждены оригиналами документов</div>
              {!certs.length ? <div className="tab-state">Сертификаты пока не добавлены</div> : null}
              {certs.map((cert) => (
                <article key={cert.id} className="cert" onClick={() => openCertificateDoc(cert)} role="button" tabIndex={0} onKeyDown={(e) => (e.key === "Enter" ? openCertificateDoc(cert) : null)}>
                  <span className="cert-ic">{cert.icon}</span>
                  <span>
                    <div className="cert-nm">{cert.name}</div>
                    {cert.org ? <div className="cert-org">{cert.org}</div> : null}
                    <div className="cert-yr">
                      {cert.year}
                      {cert.verified ? " · ✓ Проверено платформой" : ""}
                    </div>
                  </span>
                  <span className="cert-hint">{cert.verified ? "Проверено" : "Статус"}</span>
                </article>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {docModalOpen && activeCertificate ? (
        <div className="doc-modal open" onClick={(event) => (event.target === event.currentTarget ? closeCertificateDoc() : null)}>
          <div className="doc-wrap">
            <div className="doc-head">
              <div className="doc-title">{activeCertificate.name}</div>
              <button type="button" className="doc-close" onClick={closeCertificateDoc}>✕</button>
            </div>
            <div className={`doc-frame ${certPreviewUrl ? "doc-frame--media" : ""}`.trim()}>
              {certPreviewUrl ? (
                certPreviewIsImage ? (
                  <img
                    src={certPreviewUrl}
                    alt={activeCertificate.name || "Сертификат"}
                    className="doc-media"
                  />
                ) : (
                  <>
                    {certPreviewCanEmbedFrame ? (
                      <iframe
                        src={certPreviewUrl}
                        title={activeCertificate.name || "Сертификат"}
                        className={`doc-media ${certPreviewIsPdf ? "doc-media--pdf" : "doc-media--doc"}`.trim()}
                      />
                    ) : (
                      <div className="doc-inline-note">
                        Предпросмотр в браузере ограничен политикой безопасности источника. Откройте документ в новой вкладке.
                      </div>
                    )}
                    <a
                      className="doc-open-link"
                      href={certPreviewUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Открыть документ
                    </a>
                  </>
                )
              ) : (
                <div className="doc-verify-note">
                  <div className="doc-verify-note__name">{activeCertificate.name || "Документ об образовании"}</div>
                  <div className="doc-verify-note__title">
                    {activeCertificate.verified ? "Образование подтверждено" : "Документ отправлен на проверку"}
                  </div>
                  <div className="doc-verify-note__text">
                    {activeCertificate.verified
                      ? "Документ проверен командой Бюро Души"
                      : "После модерации здесь отобразится статус подтверждения документа."}
                  </div>
                </div>
              )}
            </div>
            <div className="doc-foot">
              <div className="doc-vl">{activeCertificate.verified ? "✓ Подтверждено платформой" : "Оригинал у специалиста"}</div>
            </div>
          </div>
        </div>
      ) : null}

      <Modal
        open={bookingCalendarOpen}
        title={`Календарь записи · ${name}`}
        onClose={() => setBookingCalendarOpen(false)}
        size="lg"
        className="psy-booking-modal"
        cardClassName="psy-booking-modal__card"
      >
        {calendarPsychologist ? (
          <BookingCalendar
            psychologist={calendarPsychologist}
            mode="CLIENT"
            allowDayOff={false}
          />
        ) : null}
      </Modal>

      {storyViewerOpen && activeStory ? (
        <div className={`story-modal ${storyViewerOpen ? "open" : ""}`} role="dialog" aria-modal="true" onClick={(event) => (event.target === event.currentTarget ? closeStoryViewer() : null)}>
          <div ref={storyParticlesLayerRef} id="sParticles" className="story-particles-layer story-particles-layer--modal" />
          <div
            ref={storyCardRef}
            className="story-modal-card story-card"
            id="storyCard"
            style={storyCardStyle}
            onPointerDown={handleStoryPointerDown}
            onPointerMove={handleStoryPointerMove}
            onPointerUp={finishStoryPointer}
            onPointerCancel={finishStoryPointer}
            onPointerLeave={finishStoryPointer}
          >
            <div className="swipe-hint" />
            <div className="story-progress-row sp-row">
              {storiesFeed.map((story, idx) => {
                const isDone = idx < activeStoryIndex;
                const isActive = idx === activeStoryIndex;
                return (
                  <div key={`story-progress-${story.id}`} className="story-prog sp">
                    <div
                      className={`story-prog-fill sp-fill ${isDone ? "done sp-done" : ""} ${isActive ? "active sp-active" : ""} ${isActive && storyPausedEffective ? "paused" : ""}`.trim()}
                      style={isActive ? { animationDuration: `${storyDurationMs}ms`, animationPlayState: storyPausedEffective ? "paused" : "running" } : undefined}
                    />
                  </div>
                );
              })}
            </div>
            <div className={`s-expiry-bar ${activeStoryExpiry.showBar ? "vis" : ""}`} id="sExpiryBar">
              <div className="s-expiry-fill" id="sExpiryFill" style={{ width: `${activeStoryExpiry.barPercent}%` }} />
            </div>
            <div className="story-modal-head s-head">
              <div className="story-modal-av">{initials}</div>
              <div className="s-author-block">
                <div className="story-modal-name">{activeStory.author || name}</div>
                <div className="s-meta-row">
                  <div className="story-modal-time s-time">{activeStory.timeLabel || "сейчас"}</div>
                  {activeStoryExpiry.badgeText ? (
                    <span className={`s-expiry ${activeStoryExpiry.badgeClass || ""}`.trim()}>{activeStoryExpiry.badgeText}</span>
                  ) : null}
                </div>
              </div>
              <button
                ref={storyActionsBtnRef}
                type="button"
                className={`s-actions-btn ${storyMenuOpen ? "is-open" : ""}`.trim()}
                id="sActionsBtn"
                aria-haspopup="menu"
                aria-expanded={storyMenuOpen}
                onClick={toggleStoryMenu}
              >
                ⋯
              </button>
              <button type="button" className="story-modal-close s-close" onClick={closeStoryViewer} aria-label="Закрыть историю">
                ✕
              </button>
            </div>
            <div className="s-story-title" id="sStoryTitle">
              <div className="s-story-title-text" id="sStoryTitleText">
                {activeStory.label || shortStoryLabel(activeStory.title || "История")}
              </div>
            </div>
            <div
              ref={storyActionsMenuRef}
              className={`s-actions-menu ${storyMenuOpen ? "open" : ""}`.trim()}
              id="sActionsMenu"
              role="menu"
            >
              {activeStory.mine ? (
                <>
                  <button type="button" className="s-menu-item accent" onClick={editCurrentStory}>
                    <span className="s-menu-ico">✏️</span> Редактировать сторис
                  </button>
                  <button type="button" className="s-menu-item" onClick={toggleSaveStory}>
                    <span className="s-menu-ico">{activeStory.saved ? "🔖" : "📌"}</span>
                    {activeStory.saved ? "Убрать из сохраненных" : "Сохранить навсегда"}
                  </button>
                  <button type="button" className="s-menu-item" onClick={() => setStoryExpiry(24)}>
                    <span className="s-menu-ico">⏱</span> Показывать 24 часа
                  </button>
                  <button type="button" className="s-menu-item" onClick={copyStoryLink}>
                    <span className="s-menu-ico">🔗</span> Скопировать ссылку
                  </button>
                  <button type="button" className="s-menu-item danger" onClick={deleteStory}>
                    <span className="s-menu-ico">🗑</span> Удалить сторис
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="s-menu-item" onClick={copyStoryLink}>
                    <span className="s-menu-ico">🔗</span> Скопировать ссылку
                  </button>
                  <button type="button" className="s-menu-item" onClick={shareStory}>
                    <span className="s-menu-ico">↗</span> Поделиться
                  </button>
                  <button type="button" className="s-menu-item danger" onClick={reportStory}>
                    <span className="s-menu-ico">🚩</span> Пожаловаться
                  </button>
                </>
              )}
            </div>
            <div className="story-modal-body s-body">
              <div className="story-modal-bg s-bg" style={{ background: activeStoryPayloadBackground || activeStory.background || storyBackgroundByIndex(activeStoryIndex) }} />
              {activeStoryPayloadBgImage ? (
                <img className="story-modal-bg s-bg" src={activeStoryPayloadBgImage} alt="" />
              ) : null}
              {(activeStoryPayloadMedia?.type || activeStory.mediaType) === "image" && (activeStoryPayloadMedia?.url || activeStory.mediaUrl) ? (
                <img className="story-modal-bg s-bg" src={activeStoryPayloadMedia?.url || activeStory.mediaUrl} alt="" />
              ) : null}
              {(activeStoryPayloadMedia?.type || activeStory.mediaType) === "video" && (activeStoryPayloadMedia?.url || activeStory.mediaUrl) ? (
                <video className="story-modal-bg s-bg" src={activeStoryPayloadMedia?.url || activeStory.mediaUrl} autoPlay loop muted playsInline />
              ) : null}
              <div className="story-modal-overlay s-ov" />
              {activeStoryHasCanvas ? (
                <div className="story-rendered-objects">
                  {activeStoryRenderElements.map((element) => (
                    <div
                      key={`story-render-el-${element.id}`}
                      className={`story-rendered-object sed-obj ${element.type === "text" ? "story-rendered-text" : ""} ${element.type === "sticker" ? "story-rendered-sticker" : ""}`.trim()}
                      data-fx={element.fx || undefined}
                      data-enter={element.enter && element.enter !== "none" ? element.enter : undefined}
                      style={{
                        left: `${Math.round(element.x || 0)}px`,
                        top: `${Math.round(element.y || 0)}px`,
                        width: element.type === "image" && element.w ? `${Math.round(element.w)}px` : undefined,
                        transform: `rotate(${Number(element.rot || 0)}deg)`,
                        "--obj-r": `${Number(element.rot || 0)}deg`,
                        "--fx-spd": element.fxParams?.spd ? `${element.fxParams.spd}s` : undefined,
                        "--fx-sc": element.fxParams?.sc !== undefined ? String(element.fxParams.sc) : undefined,
                        "--fx-d": element.fxParams?.d !== undefined ? `-${element.fxParams.d}px` : undefined,
                        "--fx-ang": element.fxParams?.ang !== undefined ? `${element.fxParams.ang}deg` : undefined,
                        fontFamily: element.type === "text" ? element.font || "Onest, sans-serif" : undefined,
                        fontSize: element.type === "text" ? `${Math.round(element.size || 24)}px` : undefined,
                        color: element.type === "text" ? element.color || "#ffffff" : undefined,
                      }}
                    >
                      {element.type === "text" ? (
                        <>
                          {element.textData?.title ? <div className="story-rendered-title">{element.textData.title}</div> : null}
                          {element.textData?.body ? <div className="story-rendered-body">{element.textData.body}</div> : null}
                          {!element.textData?.title && !element.textData?.body ? element.text : null}
                        </>
                      ) : null}
                      {element.type === "sticker" ? element.emoji : null}
                      {element.type === "image" ? (
                        <img
                          src={element.imgData?.url}
                          className="story-rendered-image"
                          alt=""
                          draggable={false}
                          style={{
                            borderRadius: element.imgData?.radius || "8px",
                            filter: element.imgData?.filter && element.imgData.filter !== "none" ? element.imgData.filter : undefined,
                          }}
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
              <div className="story-content s-content">
                {!activeStoryHasCanvas ? <span className="story-emoji-big s-emoji">{activeStory.emoji || "✨"}</span> : null}
                {!activeStoryHasCanvas ? <div className="story-title s-title">{activeStory.title || "История"}</div> : null}
                {!activeStoryHasCanvas && activeStory.text ? <div className="story-text s-text">{activeStory.text}</div> : null}
                <button type="button" className="story-cta s-cta" onClick={() => handleStoryAction(activeStory)}>
                  {activeStory.ctaLabel || "Открыть"} →
                </button>
              </div>
            </div>

            <div className="story-tap-left s-tap-l" onClick={(event) => { event.stopPropagation(); goStoryPrev(); }} />
            <div className="story-tap-right s-tap-r" onClick={(event) => { event.stopPropagation(); goStoryNext(); }} />

            <div className="story-modal-foot s-foot">
              <div className="story-reactions s-reactions">
                {STORY_REACTION_EMOJIS.map((emoji) => {
                  const activeEmoji = normalizeReactionValue(activeStory?.myReaction, "").emoji || activeStory?.myReaction;
                  const active = activeEmoji === emoji;
                  return (
                    <button
                      key={`reaction-${emoji}`}
                      type="button"
                      className={`story-react-btn s-react ${active ? "is-active" : ""}`.trim()}
                      onClick={(event) => handleStoryReaction(emoji, event)}
                    >
                      {emoji}
                    </button>
                  );
                })}
              </div>
              <input
                className="story-reply s-inp"
                value={storyReply}
                onChange={(e) => setStoryReply(e.target.value)}
                onFocus={() => setStoryReplyFocused(true)}
                onBlur={() => setStoryReplyFocused(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submitStoryReply();
                  }
                }}
                placeholder="Ответить..."
              />
              <button type="button" className="story-reply-send s-send" onClick={submitStoryReply}>
                ↑
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {storyEditorOpen ? (
        <div className={`story-editor ${storyEditorOpen ? "open" : ""}`} onClick={(event) => (event.target === event.currentTarget ? closeStoryEditor() : null)}>
          <div className={`sed-modal ${storyEditorOpen ? "open" : ""}`.trim()}>
            <div
              ref={editorCanvasRef}
              className="sed-canvas-wrap"
              id="sedCanvas"
              onClick={(event) => {
                if (event.target === event.currentTarget || event.target.classList.contains("se-bg") || event.target.classList.contains("se-elements")) {
                  setSelectedEditorElementId(null);
                }
              }}
            >
              <div id="sedBg" className="se-bg" style={{ background: editorBackground, opacity: editorMedia ? 0.2 : 1 }} />
              {editorBgImage?.url ? <img className="se-bg-image" src={editorBgImage.url} alt="" /> : null}
              {editorMedia?.type === "image" && editorMedia?.url ? <img id="sedMediaBg" className="se-media-bg" src={editorMedia.url} alt="" /> : null}
              {editorMedia?.type === "video" && editorMedia?.url ? (
                <video id="sedMediaBg" className="se-media-bg" src={editorMedia.url} autoPlay loop muted playsInline />
              ) : null}
              <div className="sed-vignette" />
              <div id="sedObjects" className="se-elements">
                {editorElements.map((element) => (
                  <div
                    key={element.id}
                    id={`se-el-${element.id}`}
                    className={`sed-obj se-element ${element.type === "text" ? `se-text-el style-${element.style || "plain"}` : ""} ${element.type === "sticker" ? "se-sticker-el" : ""} ${selectedEditorElementId === element.id ? "selected" : ""}`.trim()}
                    data-type={element.type}
                    data-fx={element.fx || undefined}
                    data-enter={element.enter && element.enter !== "none" ? element.enter : undefined}
                    data-exit-playing={editorExitPreviewFor === element.id && (element.imgData?.exit || element.exit) && (element.imgData?.exit || element.exit) !== "none" ? (element.imgData?.exit || element.exit) : undefined}
                    style={{
                      left: `${Math.round(element.x || 0)}px`,
                      top: `${Math.round(element.y || 0)}px`,
                      fontFamily: element.type === "text" ? element.font || "Manrope, sans-serif" : undefined,
                      fontSize: element.type === "text" ? `${Math.round(element.size || 24)}px` : undefined,
                      color: element.type === "text" ? element.color || "#ffffff" : undefined,
                      width: element.type === "image" && element.w ? `${Math.round(element.w)}px` : undefined,
                      transform: `rotate(${Number(element.rot || 0)}deg)`,
                      "--obj-r": `${Number(element.rot || 0)}deg`,
                      "--fx-spd": element.fxParams?.spd ? `${element.fxParams.spd}s` : undefined,
                      "--fx-sc": element.fxParams?.sc !== undefined ? String(element.fxParams.sc) : undefined,
                      "--fx-d": element.fxParams?.d !== undefined ? `-${element.fxParams.d}px` : undefined,
                      "--fx-ang": element.fxParams?.ang !== undefined ? `${element.fxParams.ang}deg` : undefined,
                    }}
                    onPointerDown={(event) => startEditorDrag(event, element.id)}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedEditorElementId(element.id);
                      if (element.type === "text") setEditorPane("text");
                      if (element.type === "image") setEditorPane("img");
                    }}
                  >
                    {element.type === "text" ? (
                      <>
                        {element.textData?.title ? (
                          <div style={{ fontFamily: element.textData.ff || element.font || "Onest, sans-serif", fontSize: `${Math.round(element.textData.sz || element.size || 24)}px`, fontWeight: 700, color: element.textData.color || element.color || "#fff", lineHeight: 1.25, textShadow: "0 2px 8px rgba(0,0,0,.5)", wordBreak: "break-word" }}>
                            {element.textData.title}
                          </div>
                        ) : null}
                        {element.textData?.body ? (
                          <div style={{ fontFamily: "Onest, sans-serif", fontSize: `${Math.round((element.textData.sz || element.size || 24) * 0.62)}px`, color: element.textData.color || element.color || "#fff", lineHeight: 1.5, marginTop: 5, textShadow: "0 1px 5px rgba(0,0,0,.45)", wordBreak: "break-word" }}>
                            {element.textData.body}
                          </div>
                        ) : null}
                        {!element.textData?.title && !element.textData?.body ? element.text : null}
                      </>
                    ) : null}
                    {element.type === "sticker" ? element.emoji : null}
                    {element.type === "image" ? (
                      <img
                        src={element.imgData?.url}
                        className="sed-img-obj"
                        alt=""
                        draggable={false}
                        style={{
                          borderRadius: element.imgData?.radius || "8px",
                          filter: element.imgData?.filter && element.imgData.filter !== "none" ? element.imgData.filter : undefined,
                        }}
                      />
                    ) : null}
                    <button type="button" className="se-delete-btn sed-obj-del" onClick={(event) => { event.stopPropagation(); removeEditorElement(element.id); }}>
                      ×
                    </button>
                    <button
                      type="button"
                      className="se-rotate-btn sed-obj-rot-handle"
                      onPointerDown={(event) => startEditorDrag(event, element.id, "rotate")}
                      aria-label="Повернуть объект"
                      title="Удерживайте и тяните для поворота"
                    >
                      ↻
                    </button>
                    {element.type === "text" ? (
                      <button
                        type="button"
                        className="sed-obj-edit"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedEditorElementId(element.id);
                          setEditorPane("text");
                          setEditorTextTitle(element.textData?.title || "");
                          setEditorTextInput(element.textData?.body || "");
                          setEditorTextSize(Number(element.textData?.sz || element.size || 24));
                          setEditorTextColor(element.textData?.color || element.color || "#ffffff");
                          setEditorFontFamily(element.textData?.ff || element.font || "Onest, sans-serif");
                        }}
                      >
                        Aa
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>

              <div className="se-preview-head">
                <div className="se-preview-av">{initials}</div>
                <div className="se-preview-name">{name}</div>
              </div>
              <div className="sed-canvas-top">
                <button type="button" className="sed-close" onClick={closeStoryEditor}>✕</button>
                <button type="button" className="sed-pub" onClick={publishStoryDraft}>Опубликовать →</button>
              </div>
              <div className="sed-canvas-hint" id="sedHint" style={{ opacity: editorElements.length ? 0 : 1 }}>Нажмите + чтобы добавить объекты →</div>
              {videoBadgeVisible ? <div className="se-video-badge">🎬 Видео загружено</div> : null}
            </div>

            <div className="sed-panel">
              <div className="sed-tabs">
                <button type="button" className={`sed-tab ${editorPane === "bg" ? "on" : ""}`.trim()} onClick={() => setEditorPane("bg")}>🎨<br />Фон</button>
                <button type="button" className={`sed-tab ${editorPane === "media" ? "on" : ""}`.trim()} onClick={() => setEditorPane("media")}>🎬<br />Видео</button>
                <button type="button" className={`sed-tab ${editorPane === "img" ? "on" : ""}`.trim()} onClick={() => setEditorPane("img")}>🖼<br />Фото</button>
                <button type="button" className={`sed-tab ${editorPane === "text" ? "on" : ""}`.trim()} onClick={() => setEditorPane("text")}>Aa<br />Текст</button>
                <button type="button" className={`sed-tab ${editorPane === "stk" ? "on" : ""}`.trim()} onClick={() => setEditorPane("stk")}>✨<br />Стикер</button>
                <button type="button" className={`sed-tab ${editorPane === "fx" ? "on" : ""}`.trim()} onClick={() => setEditorPane("fx")}>⚡<br />Эффект</button>
              </div>

              <div className="sed-panel-body">
                <div className={`sed-pane ${editorPane === "bg" ? "on" : ""}`.trim()} id="pane-bg">
                  <div className="sed-lbl">Название сторис</div>
                  <input className="sed-inp" type="text" maxLength={24} value={editorLabel} onChange={(e) => setEditorLabel(e.target.value)} placeholder="Например: Метод, FAQ, Новое..." style={{ marginBottom: 14 }} />
                  <div className="sed-lbl">Выберите фон</div>
                  <div className="sed-bg-grid" id="sedBgGrid">
                    {editorBackgroundPalette.map((bg, idx) => (
                      <div key={`editor-bg-${idx}`} className={`sed-bg-opt ${editorBgIndex === idx ? "on" : ""}`.trim()} style={{ background: bg }} onClick={() => setEditorBgIndex(idx)} />
                    ))}
                  </div>
                  <button type="button" className="sed-img-upload-zone" style={{ marginTop: 10 }} onClick={() => triggerMediaUpload("bg-image")}>
                    <div className="sed-img-upload-ico">🖼</div>
                    <div className="sed-img-upload-lbl">Загрузить картинку фона</div>
                    <div className="sed-img-upload-sub">PNG, JPG, WebP, GIF</div>
                  </button>
                  {editorBgImage?.url ? (
                    <button
                      type="button"
                      onClick={() => setEditorBgImage(null)}
                      style={{ width: "100%", marginTop: 10, padding: 9, background: "rgba(255,80,80,.12)", border: "1px solid rgba(255,80,80,.28)", borderRadius: 9, color: "#ff6b6b", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "'Onest',sans-serif" }}
                    >
                      ✕ Удалить фон
                    </button>
                  ) : null}
                </div>

                <div className={`sed-pane ${editorPane === "media" ? "on" : ""}`.trim()} id="pane-media">
                  <div className="sed-lbl">Видео на фон</div>
                  <div className="sed-media-grid">
                    <button type="button" className="sed-media-btn" onClick={() => triggerMediaUpload("video")}><div className="ico">🎬</div><div className="lbl">Видео</div></button>
                    <button type="button" className="sed-media-btn" onClick={toggleCamera}><div className="ico">🔴</div><div className="lbl">Live</div></button>
                  </div>
                  {editorMedia?.type === "video" ? (
                    <button
                      type="button"
                      onClick={() => setEditorMedia(null)}
                      id="sedMediaClrWrap"
                      style={{ width: "100%", padding: 9, background: "rgba(255,80,80,.12)", border: "1px solid rgba(255,80,80,.28)", borderRadius: 9, color: "#ff6b6b", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "'Onest',sans-serif" }}
                    >
                      ✕ Удалить видео
                    </button>
                  ) : null}
                </div>

                <div className={`sed-pane ${editorPane === "img" ? "on" : ""}`.trim()} id="pane-img">
                  <button type="button" className="sed-img-upload-zone" onClick={() => triggerMediaUpload("image")}>
                    <div className="sed-img-upload-ico">📸</div>
                    <div className="sed-img-upload-lbl">Нажмите для загрузки</div>
                    <div className="sed-img-upload-sub">PNG, JPG, WebP, GIF</div>
                  </button>
                  <div className={`sed-edit-banner ${selectedEditorObject?.type === "image" ? "vis" : ""}`.trim()} id="sedImgEditBanner">
                    <div className="sed-edit-banner-label"><span>🖼</span> Настройки изображения</div>
                    <div className="sed-lbl" style={{ marginTop: 4 }}>Размер</div>
                    <div className="sed-img-size-row">
                      <span className="sed-img-size-lbl">Ширина</span>
                      <input
                        className="sed-slider"
                        id="sedImgSzSlider"
                        type="range"
                        min="20"
                        max="100"
                        value={selectedEditorObject?.type === "image" ? Number(selectedEditorObject.imgData?.sz || 60) : 60}
                        onChange={(e) => {
                          const val = Number(e.target.value) || 60;
                          if (!selectedEditorElementId) return;
                          const canvasW = editorCanvasRef.current?.clientWidth || 360;
                          updateEditorElement(selectedEditorElementId, (item) => {
                            if (item.type !== "image") return item;
                            return { w: Math.max(80, Math.round((canvasW * val) / 100)), imgData: { ...(item.imgData || {}), sz: val } };
                          });
                        }}
                      />
                      <span className="sed-fx-val" id="sedImgSzVal">{selectedEditorObject?.type === "image" ? `${Number(selectedEditorObject.imgData?.sz || 60)}%` : "60%"}</span>
                    </div>
                    <div className="sed-lbl">Форма</div>
                    <div className="sed-radius-row">
                      {["8px", "16px", "50%", "0px"].map((radius) => (
                        <button
                          key={`radius-${radius}`}
                          type="button"
                          className={`sed-radius-btn ${selectedEditorObject?.type === "image" && (selectedEditorObject.imgData?.radius || "8px") === radius ? "on" : ""}`.trim()}
                          data-r={radius}
                          onClick={() => {
                            if (!selectedEditorElementId) return;
                            updateEditorElement(selectedEditorElementId, (item) => (item.type === "image" ? { imgData: { ...(item.imgData || {}), radius } } : item));
                          }}
                        >
                          <div className="r-preview" />
                        </button>
                      ))}
                    </div>
                    <div className="sed-lbl">Фильтр</div>
                    <div className="sed-filter-row">
                      {[
                        { value: "none", cls: "", label: "Оригинал" },
                        { value: "grayscale(100%)", cls: "f-bw", label: "Ч/Б" },
                        { value: "sepia(80%)", cls: "f-sepia", label: "Сепия" },
                        { value: "brightness(1.3) contrast(1.1)", cls: "f-bright", label: "Яркий" },
                        { value: "blur(2px)", cls: "f-blur-p", label: "Блюр" },
                        { value: "hue-rotate(180deg)", cls: "f-neon", label: "Неон" },
                      ].map((filterItem) => (
                        <button
                          key={`filter-${filterItem.value}`}
                          type="button"
                          className={`sed-filter-btn ${selectedEditorObject?.type === "image" && (selectedEditorObject.imgData?.filter || "none") === filterItem.value ? "on" : ""}`.trim()}
                          onClick={() => {
                            if (!selectedEditorElementId) return;
                            updateEditorElement(selectedEditorElementId, (item) => (item.type === "image" ? { imgData: { ...(item.imgData || {}), filter: filterItem.value } } : item));
                          }}
                        >
                          <div className={`f-swatch ${filterItem.cls}`.trim()} />
                          <span className="sed-filter-lbl">{filterItem.label}</span>
                        </button>
                      ))}
                    </div>
                    <div className="sed-lbl">Эффект появления</div>
                    <div className="sed-anim-row" id="sedImgEnterRow">
                      {[
                        { key: "none", ico: "⛔", name: "Нет" },
                        { key: "fade", ico: "🌫", name: "Плавно" },
                        { key: "zoom", ico: "🔍", name: "Зум" },
                        { key: "pop", ico: "💥", name: "Поп" },
                        { key: "slide-up", ico: "⬆️", name: "Снизу" },
                        { key: "flip", ico: "🔄", name: "Переворот" },
                        { key: "swing", ico: "🎸", name: "Маятник" },
                        { key: "blur", ico: "✨", name: "Размытие" },
                      ].map((enterItem) => (
                        <button key={`img-enter-${enterItem.key}`} type="button" className={`sed-anim-btn ${((selectedEditorObject?.imgData?.enter || "none") === enterItem.key) ? "on" : ""}`.trim()} onClick={() => applyEnterAnimation(enterItem.key)}>
                          <span className="sed-anim-ico">{enterItem.ico}</span>
                          <span className="sed-anim-name">{enterItem.name}</span>
                        </button>
                      ))}
                    </div>
                    <div className="sed-lbl" style={{ marginTop: 10 }}>Эффект исчезновения</div>
                    <div className="sed-anim-row" id="sedImgExitRow">
                      {[
                        { key: "none", ico: "⛔", name: "Нет" },
                        { key: "fade", ico: "🌫", name: "Угасание" },
                        { key: "slide-up", ico: "⬆️", name: "Улетает" },
                        { key: "shrink", ico: "🔻", name: "Сжатие" },
                        { key: "explode", ico: "💣", name: "Взрыв" },
                        { key: "blur", ico: "✨", name: "Размытие" },
                      ].map((exitItem) => (
                        <button key={`img-exit-${exitItem.key}`} type="button" className={`sed-anim-btn ${((selectedEditorObject?.imgData?.exit || "none") === exitItem.key) ? "on" : ""}`.trim()} onClick={() => applyExitAnimation(exitItem.key)}>
                          <span className="sed-anim-ico">{exitItem.ico}</span>
                          <span className="sed-anim-name">{exitItem.name}</span>
                        </button>
                      ))}
                    </div>
                    <div style={{ marginTop: 10, display: "flex", gap: 7 }}>
                      <button type="button" onClick={previewExitAnimation} style={{ flex: 1, padding: 8, background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 8, color: "rgba(255,255,255,.6)", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "'Onest',sans-serif" }}>
                        ▶ Превью исчезновения
                      </button>
                    </div>
                  </div>
                </div>

                <div className={`sed-pane ${editorPane === "text" ? "on" : ""}`.trim()} id="pane-text">
                  <div className={`sed-edit-banner ${selectedEditorObject?.type === "text" ? "vis" : ""}`.trim()} id="sedTxtEditBanner">
                    <div className="sed-edit-banner-label"><span>✏️</span> Редактировать объект</div>
                    <input className="sed-inp title-ff" type="text" value={editorTextTitle} onChange={(e) => setEditorTextTitle(e.target.value)} placeholder="Заголовок..." maxLength={60} style={{ marginBottom: 7 }} />
                    <textarea className="sed-inp" value={editorTextInput} onChange={(e) => setEditorTextInput(e.target.value)} placeholder="Текст..." maxLength={180} style={{ minHeight: 52, marginBottom: 7 }} />
                    <button type="button" className="sed-edit-apply" onClick={applySelectedTextEdit}>✓ Применить изменения</button>
                  </div>
                  <div className="sed-lbl">Шрифт</div>
                  <div className="sed-font-row">
                    {[
                      { value: "Unbounded, sans-serif", label: "Unb", style: { fontFamily: "'Unbounded',sans-serif", fontSize: 10 } },
                      { value: "Onest, sans-serif", label: "Sans", style: { fontFamily: "'Onest',sans-serif" } },
                      { value: "Georgia, serif", label: "Serif", style: { fontFamily: "Georgia,serif" } },
                    ].map((font) => (
                      <button key={font.value} type="button" className={`sed-ff-btn ${editorFontFamily === font.value ? "on" : ""}`.trim()} onClick={() => setEditorFontFamily(font.value)} style={font.style}>
                        {font.label}
                      </button>
                    ))}
                  </div>
                  <div className="sed-lbl">Размер</div>
                  <div className="sed-sz-row">
                    <span className="sed-sz-label" style={{ fontSize: 10 }}>A</span>
                    <input className="sed-slider" type="range" min="12" max="48" value={editorTextSize} onChange={(e) => setEditorTextSize(Number(e.target.value) || 24)} />
                    <span className="sed-sz-label" style={{ fontSize: 16 }}>A</span>
                  </div>
                  <div className="sed-lbl">Цвет текста</div>
                  <div style={{ display: "flex", gap: 7, marginBottom: 10, flexWrap: "wrap" }}>
                    {["#ffffff", "#5AEAA0", "#A89DE8", "#FFD166", "#FF6B6B", "#111111"].map((color) => (
                      <button
                        key={color}
                        type="button"
                        aria-label={`color-${color}`}
                        onClick={() => setEditorTextColor(color)}
                        style={{ width: 26, height: 26, borderRadius: "50%", background: color, cursor: "pointer", border: editorTextColor === color ? "2.5px solid rgba(255,255,255,.6)" : "2px solid transparent" }}
                      />
                    ))}
                    <input type="color" value={editorTextColor} style={{ width: 26, height: 26, borderRadius: "50%", border: "2px solid transparent", cursor: "pointer", padding: 0, background: "none" }} onChange={(e) => setEditorTextColor(e.target.value)} title="Свой цвет" />
                  </div>
                  <div className="sed-lbl">Текст</div>
                  <input className="sed-inp title-ff" type="text" value={editorTextTitle} onChange={(e) => setEditorTextTitle(e.target.value)} placeholder="Заголовок..." maxLength={60} />
                  <textarea className="sed-inp" value={editorTextInput} onChange={(e) => setEditorTextInput(e.target.value)} placeholder="Введите текст..." maxLength={180} />
                  <button type="button" className="sed-add-btn" onClick={addTextElement}>+ Добавить на холст</button>
                  <button type="button" className="sed-clr-btn" onClick={() => { setEditorTextTitle(""); setEditorTextInput(""); }}>Очистить поля</button>
                </div>

                <div className={`sed-pane ${editorPane === "stk" ? "on" : ""}`.trim()} id="pane-stk">
                  <div className="sed-lbl">Стикеры</div>
                  <div className="sed-stk-grid" id="sedStkGrid">
                    {editorStickerPalette.map((sticker, idx) => (
                      <button key={`sticker-${idx}`} type="button" className="sed-stk-btn" onClick={() => addStickerElement(sticker)}>
                        {sticker}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={`sed-pane ${editorPane === "fx" ? "on" : ""}`.trim()} id="pane-fx">
                  <div className="sed-lbl" id="sedFxHint">Выберите объект → выберите эффект</div>
                  <div className="sed-fx-row" id="sedFxList">
                    {editorFxCards.map((fx) => {
                      const isActive = selectedEditorObject?.fx === fx.key;
                      const fxParams = selectedEditorObject?.fxParams || {};
                      return (
                        <div key={fx.key} className={`sed-fx-card ${isActive ? "on" : ""}`.trim()} data-fx={fx.key} onClick={() => applyFxPreset(fx.key)}>
                          <button type="button" className="sed-fx-btn">
                            <div className="sed-fx-ico">{fx.ico}</div>
                            <div className="sed-fx-info">
                              <div className="sed-fx-name">{fx.name}</div>
                              <div className="sed-fx-desc">{fx.desc}</div>
                            </div>
                            <div className="sed-fx-badge">{fx.badge}</div>
                          </button>
                          {isActive ? (
                            <div className="sed-fx-settings" onClick={(event) => event.stopPropagation()}>
                              {fx.params.map((param) => {
                                const current = Number(fxParams[param.key] ?? editorFxDefaults[fx.key]?.[param.key] ?? 0);
                                const sliderValue = param.key === "spd" ? Math.round(current * 10) : param.key === "sc" ? Math.round(current * 100) : Math.round(current);
                                const displayValue = param.key === "spd" ? `${current.toFixed(1)}s` : param.key === "ang" ? `${Math.round(current)}°` : param.key === "d" ? `${Math.round(current)}px` : current.toFixed(2);
                                return (
                                  <div key={`${fx.key}-${param.key}`} className="sed-fx-setting-row">
                                    <span className="sed-fx-setting-lbl">{param.label}</span>
                                    <input className="sed-fx-slider" type="range" min={param.min} max={param.max} step={param.step} value={sliderValue} onChange={(e) => setFxParam(param.key, e.target.value)} />
                                    <span className="sed-fx-val">{displayValue}</span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                    <div className={`sed-fx-card ${!selectedEditorObject?.fx ? "on" : ""}`.trim()} data-fx="none" onClick={() => applyFxPreset("none")} style={{ borderColor: "rgba(255,80,80,.2)", marginTop: 4 }}>
                      <button type="button" className="sed-fx-btn">
                        <div className="sed-fx-ico">⛔</div>
                        <div className="sed-fx-info">
                          <div className="sed-fx-name">Без эффекта</div>
                          <div className="sed-fx-desc">Снять анимацию с объекта</div>
                        </div>
                        <div className="sed-fx-badge">Off</div>
                      </button>
                    </div>
                  </div>
                  <div className="sed-anim-section" style={{ marginTop: 12 }}>
                    <div className="sed-lbl">Появление объекта</div>
                    <div className="sed-anim-row" id="sedEnterRow">
                      {[
                        { key: "none", ico: "⛔", name: "Нет" },
                        { key: "fade", ico: "🌫", name: "Плавно" },
                        { key: "zoom", ico: "🔍", name: "Зум" },
                        { key: "pop", ico: "💥", name: "Поп" },
                        { key: "slide-up", ico: "⬆️", name: "Снизу" },
                        { key: "slide-dn", ico: "⬇️", name: "Сверху" },
                        { key: "flip", ico: "🔄", name: "Флип" },
                        { key: "blur", ico: "✨", name: "Размытие" },
                      ].map((enterItem) => (
                        <button key={`fx-enter-${enterItem.key}`} type="button" className={`sed-anim-btn ${((selectedEditorObject?.enter || "none") === enterItem.key) ? "on" : ""}`.trim()} onClick={() => applyEnterAnimation(enterItem.key)}>
                          <span className="sed-anim-ico">{enterItem.ico}</span>
                          <span className="sed-anim-name">{enterItem.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="sed-anim-section" style={{ marginTop: 10 }}>
                    <div className="sed-lbl">Исчезновение объекта</div>
                    <div className="sed-anim-row" id="sedExitRow">
                      {[
                        { key: "none", ico: "⛔", name: "Нет" },
                        { key: "fade", ico: "🌫", name: "Угасание" },
                        { key: "slide-up", ico: "⬆️", name: "Улетает" },
                        { key: "shrink", ico: "🔻", name: "Сжатие" },
                        { key: "explode", ico: "💣", name: "Взрыв" },
                        { key: "blur", ico: "✨", name: "Размытие" },
                      ].map((exitItem) => (
                        <button key={`fx-exit-${exitItem.key}`} type="button" className={`sed-anim-btn ${((selectedEditorObject?.exit || "none") === exitItem.key) ? "on" : ""}`.trim()} onClick={() => applyExitAnimation(exitItem.key)}>
                          <span className="sed-anim-ico">{exitItem.ico}</span>
                          <span className="sed-anim-name">{exitItem.name}</span>
                        </button>
                      ))}
                    </div>
                    <button type="button" onClick={previewExitAnimation} style={{ width: "100%", marginTop: 8, padding: 8, background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 8, color: "rgba(255,255,255,.55)", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "'Onest',sans-serif" }}>
                      ▶ Превью исчезновения
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`sed-live-overlay ${cameraOpen ? "open" : ""}`.trim()} id="sedLiveOverlay">
            <div className="sed-live-canvas">
              <video ref={cameraVideoRef} id="sedLiveVideo" className="sed-live-video" autoPlay muted playsInline />
              <div className="sed-live-top">
                <div className="sed-live-rec-dot" id="sedLiveRecDot" style={{ display: isRecording ? "flex" : "none" }}>
                  <div className="sed-rec-circle" />
                  <span id="sedLiveTimer">{recordTimeLabel}</span>
                </div>
                <button type="button" className="sed-live-close" onClick={stopCamera}>✕</button>
              </div>
              <div className="sed-live-bottom">
                <div className="sed-live-actions">
                  <button type="button" className="sed-live-flip" onClick={flipCamera}>🔄 Камера</button>
                  <button type="button" className={`sed-live-rec-btn ${isRecording ? "recording" : ""}`.trim()} id="sedLiveRecBtn" onClick={toggleRecord}>
                    <div className="sed-rec-inner" />
                  </button>
                  {editorLiveRecordedUrl ? <button type="button" className="sed-live-use-btn" id="sedLiveUseBtn" onClick={useRecordedLiveVideo}>Использовать →</button> : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <input ref={editorBgImageInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleBgImageUpload} />
      <input ref={editorImageInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageUpload} />
      <input ref={editorVideoInputRef} type="file" accept="video/*" style={{ display: "none" }} onChange={handleVideoUpload} />

      <AuthRequiredModal
        open={authRequiredOpen}
        onClose={() => setAuthRequiredOpen(false)}
        onLogin={() => {
          setAuthRequiredOpen(false);
          const returnTo =
            `${location.pathname || ""}${location.search || ""}${location.hash || ""}` || "/";
          navigate("/login", { state: { from: returnTo } });
        }}
        title="Функция доступна после авторизации"
        message="Для этого действия нужно войти в аккаунт."
        loginLabel="Перейти ко входу"
      />
    </div>
  );
}
